import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  FiSend,
  FiUser,
  FiCpu,
  FiPaperclip,
  FiChevronDown,
  FiX,
  FiMaximize2,
  FiMinimize2,
  FiDownload,
  FiZoomIn,
  FiTrash2,
  FiInfo,
  FiShare2,
  FiCornerUpRight,
  FiEdit2,
  FiPlus,
  FiFileText,
  FiClock,
  FiMic,
  FiMicOff,
} from 'react-icons/fi';
import AppHeader from './AppHeader';
import WhatsAppTemplateSelector from './WhatsAppTemplateSelector';
import WhatsAppTemplateEditor from './WhatsAppTemplateEditor';
import WhatsAppMetaTemplateManager from './WhatsAppMetaTemplateManager';
import AIWhatsAppHelper from './AIWhatsAppHelper';
import TranslatedText from './TranslatedText';
import LanguageSelector from './LanguageSelector';
import {
  getLanguageLabel,
  isEnglishCode,
  translateText,
  clearTranslationCache,
} from '../utils/translate';
import { transcribeAudioUrl, getWhisperLanguageCode } from '../utils/voiceTranscribe';

const pollMs = 4000;

/** Parse a date string from the server or from datetime-local input into a JS Date. */
const parseDateTimeLocal = (str) => {
  if (!str) return null;
  const s = String(str).trim();
  // ISO string with explicit timezone (Z or +/-HH:mm) — let JS parse the moment in time.
  if (/Z$|[+-]\d{2}:?\d{2}$/.test(s)) {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  // Local-ish string without timezone — parse components as local time.
  const normalized = s.replace(' ', 'T');
  const [datePart, timePart] = normalized.split('T');
  if (!datePart) return null;
  const [y, m, d] = datePart.split('-').map(Number);
  const timeSegs = (timePart || '00:00:00').split(':');
  const hr = Number(timeSegs[0]) || 0;
  const min = Number(timeSegs[1]) || 0;
  const sec = timeSegs[2] ? Math.floor(Number(timeSegs[2])) || 0 : 0;
  return new Date(y, m - 1, d, hr, min, sec);
};

/** Which business line the Chats sidebar + composer use (filters threads). */
const WA_CHAT_LINE_STORAGE_KEY = 'whatsapp_chat_line_id';
/** Template sends only — pick line before sending. */
const WA_TEMPLATE_SENDER_STORAGE_KEY = 'whatsapp_template_sender_id';
/** Legacy single key — migrated once into chat + template keys. */
const WA_SENDER_LEGACY_KEY = 'whatsapp_sender_id';

function parseMetaTemplateComponents(components = []) {
  const arr = Array.isArray(components) ? components : [];
  const header = arr.find((c) => c.type === 'HEADER') || null;
  const body = arr.find((c) => c.type === 'BODY') || null;
  const footer = arr.find((c) => c.type === 'FOOTER') || null;
  const buttons = [];
  for (const c of arr) {
    if (c.type === 'BUTTONS' && Array.isArray(c.buttons)) {
      for (const b of c.buttons) {
        buttons.push(b);
      }
    }
  }

  const bodyText = String(body?.text || '');
  const bodyVars = new Set();
  bodyText.replace(/\{\{(\d+)\}\}/g, (_, n) => { bodyVars.add(Number(n)); return ''; });
  const bodyVariableCount = bodyVars.size;

  const headerText = header?.format === 'TEXT' ? String(header?.text || '') : '';
  const headerHasVariable = /\{\{\d+\}\}/.test(headerText);

  const urlButtons = buttons
    .filter((b) => b.type === 'URL')
    .map((b) => ({
      text: String(b.text || ''),
      url: String(b.url || ''),
      hasVariable: /\{\{\d+\}\}/.test(String(b.url || '')),
    }));
  const hasDynamicUrlButton = urlButtons.some((b) => b.hasVariable);

  return {
    header,
    headerText,
    headerHasVariable,
    headerFormat: String(header?.format || '').toUpperCase(),
    bodyText,
    bodyVariableCount,
    footerText: String(footer?.text || ''),
    urlButtons,
    hasDynamicUrlButton,
    example: body?.example?.body_text?.[0] || [],
  };
}
const SESSION_WINDOW_MS = 24 * 60 * 60 * 1000; // WhatsApp Cloud API: 24h from last INBOUND message

/**
 * Parse a SQLite CURRENT_TIMESTAMP string ('YYYY-MM-DD HH:MM:SS') as UTC.
 * SQLite stores in UTC but omits the 'Z' suffix, so plain new Date() misparses it
 * as local time in the browser — causing up to ±14h offset error.
 */
function parseUtcTimestamp(ts) {
  if (!ts) return null;
  const s = String(ts).trim();
  // Already has timezone info — parse as-is
  if (s.endsWith('Z') || s.includes('+') || /[+-]\d{2}:\d{2}$/.test(s)) return new Date(s);
  // SQLite format 'YYYY-MM-DD HH:MM:SS' → force UTC by appending 'Z'
  return new Date(s.replace(' ', 'T') + 'Z');
}

/**
 * Format a UTC timestamp string from SQLite into the user's local date+time.
 */
function fmtLocal(ts) {
  if (!ts) return '';
  const d = parseUtcTimestamp(ts);
  if (!d || isNaN(d.getTime())) return String(ts);
  return d.toLocaleString(undefined, {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });
}

/**
 * Calculate the Meta 24h session window status from the last inbound message timestamp.
 * Returns: { open, msLeft, label, urgency: 'ok'|'warn'|'expired' }
 */
function getSessionWindow(lastInboundAt) {
  if (!lastInboundAt) return { open: false, msLeft: 0, label: 'No customer message yet', urgency: 'expired' };
  const ts = parseUtcTimestamp(lastInboundAt);
  if (!ts || isNaN(ts.getTime())) return { open: false, msLeft: 0, label: 'No customer message yet', urgency: 'expired' };
  const expiresAt = ts.getTime() + SESSION_WINDOW_MS;
  const msLeft = expiresAt - Date.now();
  if (msLeft <= 0) return { open: false, msLeft: 0, label: 'Session expired — use template', urgency: 'expired' };
  const totalMins = Math.floor(msLeft / 60000);
  const hrs = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  const label = hrs > 0 ? `${hrs}h ${mins}m left` : `${mins}m left`;
  const urgency = msLeft < 2 * 60 * 60 * 1000 ? 'warn' : 'ok'; // warn if < 2h left
  return { open: true, msLeft, label, urgency };
}

/** Digits-only E.164-style numbers for bulk template sends (deduped, order preserved). */
function parseTemplatePhoneList(primary, bulkText) {
  const chunks = [];
  const push = (s) => {
    const t = String(s || '').trim();
    if (t) chunks.push(t);
  };
  push(primary);
  for (const line of String(bulkText || '').split(/\r?\n/)) {
    for (const seg of line.split(/[,;]/)) {
      push(seg);
    }
  }
  const seen = new Set();
  const out = [];
  for (const ch of chunks) {
    const d = ch.replace(/\D/g, '');
    if (d.length >= 8 && !seen.has(d)) {
      seen.add(d);
      out.push(d);
    }
  }
  return out;
}

// WhatsApp-style doodle chat background (encoded SVG tile)
const CHAT_DOODLE_BG = (() => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><g opacity="0.075" fill="none" stroke="#374151" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="28" cy="28" r="14"/><path d="M20 32 Q28 40 36 32"/><circle cx="23" cy="24" r="2" fill="#374151" stroke="none"/><circle cx="33" cy="24" r="2" fill="#374151" stroke="none"/><rect x="70" y="12" width="42" height="28" rx="8"/><path d="M76 40 L70 52 L83 40" fill="#374151" stroke="none"/><path d="M165 22 Q165 10 175 10 Q185 10 185 20 Q185 34 165 44 Q145 34 145 20 Q145 10 155 10 Q165 10 165 22"/><rect x="8" y="84" width="42" height="28" rx="5"/><circle cx="29" cy="98" r="9"/><rect x="18" y="79" width="14" height="7" rx="3"/><path d="M168 80 L172 92 L185 92 L175 100 L179 112 L168 105 L157 112 L161 100 L151 92 L164 92 Z" fill="#374151" stroke="none"/><rect x="84" y="112" width="26" height="44" rx="5"/><circle cx="97" cy="150" r="3" fill="#374151" stroke="none"/><line x1="91" y1="120" x2="103" y2="120"/><path d="M148 132 L148 156"/><path d="M148 132 L167 126 L167 150"/><ellipse cx="143" cy="158" rx="6" ry="5" fill="#374151" stroke="none"/><ellipse cx="162" cy="152" rx="6" ry="5" fill="#374151" stroke="none"/><path d="M20 158 L20 180 Q22 145 34 148 Q42 148 42 157 L54 157 Q63 157 61 170 Q59 180 49 180 L20 180"/><path d="M105 66 Q105 60 109 60 Q113 60 113 65 Q113 72 105 77 Q97 72 97 65 Q97 60 101 60 Q105 60 105 66"/><path d="M55 147 Q62 138 72 136 Q82 134 89 140"/><path d="M58 154 Q64 149 72 148 Q80 147 86 151"/><circle cx="72" cy="159" r="3" fill="#374151" stroke="none"/><path d="M108 8 L110 14 L117 14 L111 18 L113 24 L108 20 L103 24 L105 18 L99 14 L106 14 Z" fill="#374151" stroke="none"/></g></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
})();

const WhatsAppPage = ({ embedded = false, preselectPhone }) => {
  const [configured, setConfigured] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState('');
  const [messages, setMessages] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [stats, setStats] = useState(null);
  const [sendingTemplate, setSendingTemplate] = useState(false);
  const [composerSending, setComposerSending] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [voiceListening, setVoiceListening] = useState(false);
  const recognitionRef = useRef(null);
  const voiceFinalRef = useRef('');
  const voiceInterimRef = useRef('');
  const [voiceRecording, setVoiceRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const mediaRecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const [transcribingId, setTranscribingId] = useState(null);
  const [transcriptionResults, setTranscriptionResults] = useState({});
  const [transcribeSourceLang, setTranscribeSourceLang] = useState({});
  const micLongPressTimerRef = useRef(null);
  const micLongPressTriggeredRef = useRef(false);
  const [mediaFile, setMediaFile] = useState(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  /** @type {{ id: number, name: string, phone: string }[]} */
  const [contacts, setContacts] = useState([]);

  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [selectedWhatsappTemplateId, setSelectedWhatsappTemplateId] = useState(null);
  const [templateToEdit, setTemplateToEdit] = useState(null);
  const [showWhatsappTemplateEditor, setShowWhatsappTemplateEditor] = useState(false);
  const [showAIWhatsAppHelper, setShowAIWhatsAppHelper] = useState(false);

  const [tplPhone, setTplPhone] = useState('');
  const [tplName, setTplName] = useState('');
  const [tplLang, setTplLang] = useState('en_US');
  const [tplMode, setTplMode] = useState('document');
  const [tplDocLink, setTplDocLink] = useState('');
  const [tplDocText, setTplDocText] = useState('');
  /** Simple mode: second body placeholder ({{2}}), e.g. account_creation_confirmation_3 */
  const [tplDocText2, setTplDocText2] = useState('');
  /** Simple mode: third body placeholder ({{3}}). */
  const [tplDocText3, setTplDocText3] = useState('');
  /** Simple templates only: TEXT header variable (if any), URL button dynamic suffix (first CTA URL button). */
  const [tplHeaderParam, setTplHeaderParam] = useState('');
  const [tplUrlButtonSuffix, setTplUrlButtonSuffix] = useState('');
  const [tplSalespersonPhone, setTplSalespersonPhone] = useState('');
  const [tplAlertPhone, setTplAlertPhone] = useState('');
  const [tplBulkPhones, setTplBulkPhones] = useState('');
  const [deletingChat, setDeletingChat] = useState(false);
  const [autoReplyInfo, setAutoReplyInfo] = useState({ enabled: true, cooldown: 120 });
  /** @type {{ id: string, label: string, phone_number_id: string }[]} */
  const [waSenders, setWaSenders] = useState([]);
  const [waChatLineId, setWaChatLineId] = useState('');
  const [waTplSenderId, setWaTplSenderId] = useState('');
  const [returningToBot, setReturningToBot] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showTemplatePanel, setShowTemplatePanel] = useState(false);
  const [showQuickTemplateModal, setShowQuickTemplateModal] = useState(false);
  const [quickPhoneLocked, setQuickPhoneLocked] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);

  // WhatsApp scheduling
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [schedulePhone, setSchedulePhone] = useState('');
  const [scheduleBody, setScheduleBody] = useState('');
  const [scheduleAt, setScheduleAt] = useState('');
  const scheduleTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [scheduleTemplateId, setScheduleTemplateId] = useState('');
  const [scheduleTemplateParams, setScheduleTemplateParams] = useState(['', '', '']);
  const [scheduleHeaderParam, setScheduleHeaderParam] = useState('');
  const [scheduleUrlSuffix, setScheduleUrlSuffix] = useState('');
  const [scheduleDocLink, setScheduleDocLink] = useState('');
  const [waSchedules, setWaSchedules] = useState([]);
  const [loadingWaSchedules, setLoadingWaSchedules] = useState(false);
  const [scheduleRecipientWindow, setScheduleRecipientWindow] = useState({ inWindow: false, lastInbound: null });
  const [translateLang, setTranslateLang] = useState(() => localStorage.getItem('wa_translate_lang') || 'eng_Latn');
  const [translatedPreview, setTranslatedPreview] = useState('');
  const [translatingPreview, setTranslatingPreview] = useState(false);
  const [failedMedia, setFailedMedia] = useState(() => new Set());
  const [forwardMessage, setForwardMessage] = useState(null);
  const [editingDisplayName, setEditingDisplayName] = useState(null);
  const [displayNameInput, setDisplayNameInput] = useState('');
  const [savingDisplayName, setSavingDisplayName] = useState(false);
  /** When true, load full merged thread (both business numbers) for this customer phone. */
  const [showAllLinesInOpenChat, setShowAllLinesInOpenChat] = useState(false);
  const [mergeLinesHelpOpen, setMergeLinesHelpOpen] = useState(false);

  const chatScrollRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);

  const selectedConv = useMemo(
    () => conversations.find((c) => c.phone_e164 === selectedPhone),
    [conversations, selectedPhone]
  );
  const isAgentHandoff = selectedConv && Number(selectedConv.agent_mode) === 1;
  const normalizedSearch = searchQuery.trim().toLowerCase();

  const normalizePhoneDigits = useCallback((phone) => {
    return String(phone || '').replace(/\D/g, '');
  }, []);

  const contactNameMap = useMemo(() => {
    const map = {};
    const setIfEmpty = (key, value) => {
      if (!key || map[key]) return;
      map[key] = value;
    };
    for (const contact of contacts) {
      const digits = normalizePhoneDigits(contact.phone);
      if (!digits) continue;
      const name = String(contact.name || '').trim() || digits;
      setIfEmpty(digits, name);
      // Also index last 10 digits so contacts saved without country code still match
      if (digits.length >= 10) {
        const last10 = digits.slice(-10);
        setIfEmpty(last10, name);
      }
    }
    return map;
  }, [contacts, normalizePhoneDigits]);

  const whatsappNameMap = useMemo(() => {
    const map = {};
    for (const c of conversations) {
      const digits = normalizePhoneDigits(c.phone_e164);
      const name = String(c.display_name || c.sender_name || '').trim();
      if (digits && name && !map[digits]) {
        map[digits] = name;
      }
    }
    return map;
  }, [conversations, normalizePhoneDigits]);

  const getContactName = useCallback(
    (phone) => {
      const digits = normalizePhoneDigits(phone);
      if (!digits) return '';
      if (whatsappNameMap[digits]) return whatsappNameMap[digits];
      if (contactNameMap[digits]) return contactNameMap[digits];
      if (digits.length >= 10) {
        const last10 = digits.slice(-10);
        if (contactNameMap[last10]) return contactNameMap[last10];
      }
      return '';
    },
    [contactNameMap, whatsappNameMap, normalizePhoneDigits]
  );

  const getDisplayLabel = useCallback(
    (phone) => {
      const name = getContactName(phone);
      if (name && name !== phone) return name;
      return phone;
    },
    [getContactName]
  );

  const loadWhatsappTemplates = useCallback(async () => {
    try {
      const response = await axios.get('/api/whatsapp/templates');
      const local = Array.isArray(response.data) ? response.data : [];
      // Also fetch approved Meta templates for the selected sender and merge them as selectable presets
      let metaApproved = [];
      try {
        const params = waTplSenderId ? { sender_id: waTplSenderId } : {};
        const metaRes = await axios.get('/api/whatsapp/meta-templates', { params });
        const all = Array.isArray(metaRes.data?.templates) ? metaRes.data.templates : [];
        const seen = new Set();
        metaApproved = all
          .filter((t) => ['approved', 'active'].includes(String(t.status || '').toLowerCase()))
          .filter((t) => !local.some((l) => l.metaTemplateName === t.name && l.languageCode === t.language))
          .filter((t) => { const k = `${t.name}__${t.language}`; if (seen.has(k)) return false; seen.add(k); return true; })
          .map((t) => {
            const components = Array.isArray(t.components) ? t.components : [];
            const headerComponent = components.find((c) => c.type === 'HEADER');
            const headerFormat = String(headerComponent?.format || '').toUpperCase();
            const isMediaHeader = ['IMAGE', 'DOCUMENT', 'VIDEO'].includes(headerFormat);
            return {
              id: `meta_${t.name}_${t.language}`,
              name: `${t.name} (Meta)`,
              category: String(t.category || 'utility').toLowerCase(),
              templateMode: isMediaHeader ? 'document' : 'simple',
              metaTemplateName: t.name,
              languageCode: t.language || 'en_US',
              documentLink: '',
              documentText: '',
              components,
              _fromMeta: true,
            };
          });
      } catch (e) {
        // Meta templates endpoint may not have synced yet — ignore
      }
      setWhatsappTemplates([...local, ...metaApproved]);
    } catch (e) {
      toast.error('Could not load WhatsApp templates');
      console.error('Error loading WhatsApp templates:', e);
      setWhatsappTemplates([]);
    }
  }, [waTplSenderId]);

  const selectedWhatsappTemplate = useMemo(
    () => whatsappTemplates.find((t) => t.id === selectedWhatsappTemplateId) || null,
    [whatsappTemplates, selectedWhatsappTemplateId]
  );

  const selectedTemplateParsed = useMemo(
    () => parseMetaTemplateComponents(selectedWhatsappTemplate?.components),
    [selectedWhatsappTemplate]
  );

  const filteredConversations = useMemo(() => {
    if (!normalizedSearch) return conversations;
    return conversations.filter((c) => {
      const phone = String(c.phone_e164 || '').toLowerCase();
      if (phone.includes(normalizedSearch)) return true;
      const name = String(getContactName(c.phone_e164) || '').toLowerCase();
      if (name.includes(normalizedSearch)) return true;
      const preview = String(c.last_preview || '').toLowerCase();
      return preview.includes(normalizedSearch);
    });
  }, [conversations, normalizedSearch, getContactName]);

  const filteredMessages = useMemo(() => {
    if (!normalizedSearch) return messages;
    return messages.filter((m) => {
      const body = String(m.body || '').toLowerCase();
      if (body.includes(normalizedSearch)) return true;
      let extra = {};
      try {
        extra =
          typeof m.extra_json === 'string'
            ? JSON.parse(m.extra_json || '{}')
            : (m.extra_json || {});
      } catch {
        extra = {};
      }
      const caption = String(extra?.caption || '').toLowerCase();
      return caption.includes(normalizedSearch);
    });
  }, [messages, normalizedSearch]);

  const handleSelectWhatsappTemplate = (tpl) => {
    if (!tpl) return;
    setSelectedWhatsappTemplateId(tpl.id);
    setTplName(tpl.metaTemplateName || '');
    setTplLang(tpl.languageCode || 'en_US');
    setTplMode(tpl.templateMode || 'document');
    setTplDocLink(tpl.documentLink || '');
    setTplDocText(tpl.documentText || '');
    setTplDocText2('');
    setTplDocText3('');
    setTplSalespersonPhone('');
    setTplAlertPhone('');
    setTplBulkPhones('');
  };

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/whatsapp/status');
      setConfigured(!!data.configured);
      setAutoReplyInfo({
        enabled: data.autoReplyEnabled !== false,
        cooldown: typeof data.menuCooldownSeconds === 'number' ? data.menuCooldownSeconds : 120,
      });
      const senders = (Array.isArray(data.senders) ? data.senders : []).filter((s) => s.id === 'sales');
      setWaSenders(senders);
      if (senders.length > 0) {
        const pickValid = (saved) =>
          saved && senders.some((s) => s.id === saved)
            ? saved
            : data.default_sender_id && senders.some((s) => s.id === data.default_sender_id)
              ? data.default_sender_id
              : senders[0].id;
        let chatSaved =
          typeof window !== 'undefined' ? window.localStorage.getItem(WA_CHAT_LINE_STORAGE_KEY) : '';
        let tplSaved =
          typeof window !== 'undefined' ? window.localStorage.getItem(WA_TEMPLATE_SENDER_STORAGE_KEY) : '';
        if (!chatSaved && !tplSaved && typeof window !== 'undefined') {
          const legacy = window.localStorage.getItem(WA_SENDER_LEGACY_KEY);
          if (legacy && senders.some((s) => s.id === legacy)) {
            chatSaved = legacy;
            tplSaved = legacy;
            try {
              window.localStorage.setItem(WA_CHAT_LINE_STORAGE_KEY, legacy);
              window.localStorage.setItem(WA_TEMPLATE_SENDER_STORAGE_KEY, legacy);
            } catch {
              /* ignore */
            }
          }
        }
        setWaChatLineId(pickValid(chatSaved));
        setWaTplSenderId(pickValid(tplSaved));
      } else {
        setWaChatLineId('');
        setWaTplSenderId('');
      }
    } catch {
      setConfigured(false);
      setWaSenders([]);
      setWaChatLineId('');
      setWaTplSenderId('');
    }
  }, []);

  const loadContacts = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/contacts', { params: { limit: 2000 } });
      const list = data && Array.isArray(data.contacts)
        ? data.contacts
        : Array.isArray(data)
        ? data
        : [];
      setContacts(list);
    } catch (e) {
      toast.error('Could not load contacts');
      console.error('Error loading contacts:', e);
      setContacts([]);
    }
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      const params = {};
      if (waSenders.length > 1 && waChatLineId) {
        params.sender_id = waChatLineId;
      }
      const { data } = await axios.get('/api/whatsapp/conversations', { params });
      setConversations(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Could not load conversations');
      console.error(e);
    } finally {
      setLoadingList(false);
    }
  }, [waSenders.length, waChatLineId]);

  // In the embedded vendor preview, automatically open the conversation for the
  // current flow's phone (or the most recent conversation if no phone is set yet).
  useEffect(() => {
    if (embedded && !selectedPhone && conversations.length > 0) {
      const preDigits = String(preselectPhone || '').replace(/\D/g, '');
      const match = preDigits
        ? conversations.find((c) => c.phone_e164.replace(/\D/g, '') === preDigits)
        : null;
      setSelectedPhone(match ? match.phone_e164 : conversations[0].phone_e164);
    }
  }, [conversations, embedded, preselectPhone, selectedPhone]);

  const loadStats = useCallback(async () => {
    if (configured === false) return;
    try {
      const params = {};
      if (waSenders.length > 1 && waChatLineId) {
        params.sender_id = waChatLineId;
      }
      const { data } = await axios.get('/api/whatsapp/stats', { params });
      setStats(data || null);
    } catch (e) {
      toast.error('Could not load WhatsApp statistics');
      console.error(e);
    } finally {
      // no-op
    }
  }, [configured, waSenders.length, waChatLineId]);

  const loadMessages = useCallback(async (phone) => {
    if (!phone) {
      setMessages([]);
      return;
    }
    try {
      const params = { phone };
      if (waSenders.length > 1 && waChatLineId && !showAllLinesInOpenChat) {
        params.sender_id = waChatLineId;
      }
      const { data } = await axios.get('/api/whatsapp/messages', { params });
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      toast.error('Could not load messages');
    }
  }, [waSenders.length, waChatLineId, showAllLinesInOpenChat]);

  const persistWaChatLineId = useCallback((v) => {
    setWaChatLineId(v);
    try {
      window.localStorage.setItem(WA_CHAT_LINE_STORAGE_KEY, v);
    } catch {
      /* ignore */
    }
    setSelectedPhone('');
    setMessages([]);
  }, []);

  const persistWaTplSenderId = useCallback((v) => {
    setWaTplSenderId(v);
    try {
      window.localStorage.setItem(WA_TEMPLATE_SENDER_STORAGE_KEY, v);
    } catch {
      /* ignore */
    }
  }, []);

  const openTemplatePanel = useCallback((phone = '') => {
    setTplPhone(phone || '');
    setTplBulkPhones('');
    setShowTemplatePanel(true);
  }, []);

  const openQuickTemplateModal = useCallback((phone = '') => {
    if (waChatLineId) persistWaTplSenderId(waChatLineId);
    setTplPhone(phone || '');
    setTplBulkPhones('');
    setTplDocText('');
    setTplDocText2('');
    setTplDocText3('');
    setTplHeaderParam('');
    setTplUrlButtonSuffix('');
    setTplSalespersonPhone('');
    setTplAlertPhone('');
    setQuickPhoneLocked(!!phone);
    setShowQuickTemplateModal(true);
  }, [waChatLineId, persistWaTplSenderId]);

  useEffect(() => {
    loadStatus();
    loadWhatsappTemplates();
  }, [loadStatus, loadWhatsappTemplates]);

  useEffect(() => {
    loadContacts();
    loadConversations();
    loadStats();
    const t = setInterval(() => {
      loadContacts();
      loadConversations();
      loadStats();
    }, pollMs);
    return () => clearInterval(t);
  }, [loadContacts, loadConversations, loadStats]);

  /** Inbox filter can drop a thread from the list — clear selection so we don't show wrong-line messages. */
  useEffect(() => {
    if (!selectedPhone) return;
    if (!conversations.some((c) => c.phone_e164 === selectedPhone)) {
      setSelectedPhone('');
      setMessages([]);
    }
  }, [conversations, selectedPhone]);

  useEffect(() => {
    if (!selectedPhone) return;
    loadMessages(selectedPhone);
    const t = setInterval(() => loadMessages(selectedPhone), pollMs);
    return () => clearInterval(t);
  }, [selectedPhone, loadMessages, waChatLineId, waSenders.length, showAllLinesInOpenChat]);

  useEffect(() => {
    setShowAllLinesInOpenChat(false);
    setMergeLinesHelpOpen(false);
  }, [selectedPhone]);

  useEffect(() => {
    if (!mergeLinesHelpOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setMergeLinesHelpOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mergeLinesHelpOpen]);

  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    if (shouldAutoScrollRef.current) {
      el.scrollTop = el.scrollHeight;
      setShowScrollDown(false);
    } else {
      setShowScrollDown(true);
    }
  }, [messages, selectedPhone]);

  const handleChatScroll = () => {
    const el = chatScrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const isNearBottom = distanceFromBottom < 80;
    shouldAutoScrollRef.current = isNearBottom;
    setShowScrollDown(!isNearBottom);
  };

  const jumpToLatest = () => {
    const el = chatScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    shouldAutoScrollRef.current = true;
    setShowScrollDown(false);
  };

  const normalizeDeliveryStatus = (status) =>
    String(status || '')
      .toLowerCase()
      .trim();

  const getStatusTick = (status, isBlk) => {
    const s = normalizeDeliveryStatus(status);
    if (s === 'read') return '✓✓';
    if (s === 'delivered') return '✓✓';
    if (s === 'failed') return isBlk ? '!' : '✗';
    return '✓'; // sent / pending / default
  };

  const getStatusColor = (status, isBlk) => {
    const s = normalizeDeliveryStatus(status);
    if (s === 'read') return 'text-sky-300';
    if (s === 'delivered') return 'text-slate-300';
    if (s === 'failed') return isBlk ? 'text-amber-300' : 'text-red-200';
    return 'text-white/70';
  };

  const isBlockedFailure = (extra) => {
    const e = extra?.delivery_error;
    if (!e) return false;
    const text = JSON.stringify(e).toLowerCase();
    return text.includes('block') || text.includes('undeliverable') || text.includes('invalid') || text.includes('not allowed');
  };

  const parseExtraJson = (value) => {
    if (!value) return {};
    try {
      if (typeof value === 'string') return JSON.parse(value);
      if (typeof value === 'object') return value;
      return {};
    } catch {
      return {};
    }
  };

  /** Webhook stores Meta `errors[0]` on failed delivery — show on tick hover. */
  const formatMetaDeliveryFailureTitle = (extra) => {
    const e = extra?.delivery_error;
    if (!e || typeof e !== 'object') return '';
    const code = e.code != null ? `#${e.code}` : '';
    const t = String(e.title || '').trim();
    const msg = String(e.message || '').trim();
    const one = t || msg;
    if (!one) return code ? `Failed ${code}` : '';
    return code ? `${one} (${code})` : one;
  };

  const mediaAuthQuery = (extra) => {
    const pid = extra?.phone_number_id != null ? String(extra.phone_number_id).replace(/\D/g, '') : '';
    if (pid) return `?phone_number_id=${encodeURIComponent(pid)}`;
    if (waChatLineId) {
      const row = waSenders.find((s) => s.id === waChatLineId);
      if (row?.phone_number_id) return `?phone_number_id=${encodeURIComponent(String(row.phone_number_id))}`;
    }
    return '';
  };

  const getMediaRenderData = (m) => {
    const extra = parseExtraJson(m.extra_json);
    const incomingImageId = extra?.image_id;
    const incomingDocumentId = extra?.document_id;
    const incomingVideoId = extra?.video_id;
    const incomingAudioId = extra?.audio_id;
    const outboundMediaId = extra?.media_id;
    const mq = mediaAuthQuery(extra);

    if (m.variety === 'image') {
      const id = incomingImageId || outboundMediaId;
      if (id) {
        return { type: 'image', url: `/api/whatsapp/media/${id}${mq}`, label: m.body || 'Image' };
      }
      if (typeof m.body === 'string' && /^https?:\/\//i.test(m.body)) {
        return { type: 'image', url: m.body, label: 'Image' };
      }
      return { type: 'text', text: m.body || '[image]' };
    }

    if (m.variety === 'document') {
      const id = incomingDocumentId || outboundMediaId;
      if (id) {
        return { type: 'document', url: `/api/whatsapp/media/${id}${mq}`, label: m.body || 'Document' };
      }
      if (typeof m.body === 'string' && /^https?:\/\//i.test(m.body)) {
        return { type: 'document', url: m.body, label: 'Document' };
      }
      return { type: 'text', text: m.body || '[document]' };
    }

    if (m.variety === 'video') {
      const id = incomingVideoId || outboundMediaId;
      if (id) {
        return { type: 'video', url: `/api/whatsapp/media/${id}${mq}` };
      }
      return { type: 'text', text: m.body || '[video]' };
    }

    if (m.variety === 'audio') {
      const id = incomingAudioId || outboundMediaId;
      if (id) {
        return { type: 'audio', url: `/api/whatsapp/media/${id}${mq}` };
      }
      return { type: 'text', text: m.body || '[audio]' };
    }

    return { type: 'text', text: m.body || '—' };
  };

  const handleTranslateLangChange = (code) => {
    setTranslateLang(code);
    localStorage.setItem('wa_translate_lang', code);
    clearTranslationCache();
    setTranslatedPreview('');
  };

  useEffect(() => {
    let cancelled = false;
    const text = replyText.trim();
    if (!text || isEnglishCode(translateLang)) {
      setTranslatedPreview('');
      setTranslatingPreview(false);
      return;
    }
    const timer = setTimeout(async () => {
      setTranslatingPreview(true);
      try {
        const translated = await translateText(text, translateLang);
        if (!cancelled) setTranslatedPreview(translated);
      } catch (err) {
        console.error('Translate preview:', err);
      } finally {
        if (!cancelled) setTranslatingPreview(false);
      }
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [replyText, translateLang]);

  const chooseVoiceMimeType = () => {
    const preferred = [
      { mime: 'audio/ogg;codecs=opus', ext: 'ogg' },
      { mime: 'audio/mp4', ext: 'm4a' },
      { mime: 'audio/mpeg', ext: 'mp3' },
      { mime: 'audio/aac', ext: 'aac' },
      { mime: 'audio/webm', ext: 'webm' },
    ];
    for (const p of preferred) {
      if (MediaRecorder.isTypeSupported(p.mime)) return p;
    }
    return { mime: '', ext: 'webm' };
  };

  const checkMicPermission = async () => {
    try {
      if (navigator.permissions?.query) {
        const result = await navigator.permissions.query({ name: 'microphone' });
        return result.state; // 'granted', 'denied', or 'prompt'
      }
    } catch (e) {
      console.error('Permission query error:', e);
    }
    return 'prompt';
  };

  const startVoiceRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error('Microphone access is not supported. Use HTTPS or a modern browser.');
      return;
    }
    if (!window.isSecureContext) {
      toast.error('Microphone requires HTTPS. Please access this site over https:// or test on localhost.');
      return;
    }
    const perm = await checkMicPermission();
    if (perm === 'denied') {
      toast.error('Microphone is blocked for this site. Click the lock icon in the address bar → Site settings → Microphone → Allow, then refresh.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const { mime: mimeType, ext } = chooseVoiceMimeType();
      if (!mimeType) {
        toast.error('Your browser does not support a WhatsApp-compatible audio format.');
        return;
      }
      const recorder = new MediaRecorder(stream, { mimeType });
      recordedChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(recordedChunksRef.current, { type: mimeType });
        await sendVoiceMessage(blob, ext, mimeType);
      };
      recorder.onerror = (e) => {
        console.error('MediaRecorder error:', e);
        toast.error('Recording failed');
        stopVoiceRecording();
      };
      recorder.start(100);
      mediaRecorderRef.current = recorder;
      setVoiceRecording(true);
      setRecordingSeconds(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((s) => s + 1);
      }, 1000);
    } catch (err) {
      console.error('Mic access error:', err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        toast.error('Microphone permission denied. Allow mic access and ensure the page is served over HTTPS.');
      } else {
        toast.error('Could not access microphone');
      }
    }
  };

  const stopVoiceRecording = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;
    setVoiceRecording(false);
    setRecordingSeconds(0);
  };

  const handleTranscribeAudio = async (messageId, audioUrl, sourceLang) => {
    if (transcribingId) return;
    setTranscribingId(messageId);
    try {
      toast('Transcribing voice message…', { icon: '🎙️' });
      const { transcript, source } = await transcribeAudioUrl(audioUrl, sourceLang);
      let translated = transcript;
      if (transcript && !isEnglishCode(translateLang)) {
        translated = await translateText(transcript, translateLang);
      }
      setTranscriptionResults((prev) => ({
        ...prev,
        [messageId]: {
          transcript,
          translated,
          source,
          sourceLang: getWhisperLanguageCode(sourceLang),
          targetLang: translateLang,
        },
      }));
      toast.success('Transcription complete');
    } catch (err) {
      console.error('Transcribe error:', err);
      toast.error(err?.message || 'Transcription failed');
    } finally {
      setTranscribingId(null);
    }
  };

  const sendVoiceMessage = async (audioBlob, ext = 'ogg', mimeType = '') => {
    if (!selectedPhone || configured === false) return;
    setComposerSending(true);
    try {
      const form = new FormData();
      form.append('phone', selectedPhone);
      form.append('file', audioBlob, `voice-message.${ext}`);
      if (mimeType) form.append('mime_type', mimeType);
      if (waChatLineId) form.append('sender_id', waChatLineId);
      await axios.post('/api/whatsapp/send-voice', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Voice message sent');
      loadMessages(selectedPhone);
      loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Voice send failed');
    } finally {
      setComposerSending(false);
    }
  };

  const speechRecognitionSupported = 'SpeechRecognition' in window || 'webkitSpeechRecognition' in window;

  const getSpeechRecognitionLang = () => {
    const lang = translateLang || 'eng_Latn';
    if (lang.startsWith('hin')) return 'hi-IN';
    if (lang.startsWith('tel')) return 'te-IN';
    if (lang.startsWith('tam')) return 'ta-IN';
    if (lang.startsWith('kan')) return 'kn-IN';
    if (lang.startsWith('mal')) return 'ml-IN';
    if (lang.startsWith('mar')) return 'mr-IN';
    if (lang.startsWith('ben')) return 'bn-IN';
    if (lang.startsWith('guj')) return 'gu-IN';
    if (lang.startsWith('pan')) return 'pa-IN';
    if (lang.startsWith('urd')) return 'ur-PK';
    if (lang.startsWith('arb')) return 'ar-SA';
    if (lang.startsWith('spa')) return 'es-ES';
    if (lang.startsWith('fra')) return 'fr-FR';
    if (lang.startsWith('deu')) return 'de-DE';
    if (lang.startsWith('zho')) return 'zh-CN';
    if (lang.startsWith('jpn')) return 'ja-JP';
    if (lang.startsWith('kor')) return 'ko-KR';
    if (lang.startsWith('rus')) return 'ru-RU';
    if (lang.startsWith('por')) return 'pt-PT';
    if (lang.startsWith('ita')) return 'it-IT';
    if (lang.startsWith('nld')) return 'nl-NL';
    if (lang.startsWith('tur')) return 'tr-TR';
    if (lang.startsWith('vie')) return 'vi-VN';
    if (lang.startsWith('tha')) return 'th-TH';
    return 'en-US';
  };

  const startVoiceToText = async () => {
    if (!speechRecognitionSupported) {
      toast.error('Voice-to-text is not supported in this browser.');
      return false;
    }
    if (recognitionRef.current) return true;
    if (!window.isSecureContext) {
      toast.error('Microphone requires HTTPS. Please access this site over https:// or test on localhost.');
      return false;
    }
    const perm = await checkMicPermission();
    if (perm === 'denied') {
      toast.error('Microphone is blocked for this site. Click the lock icon in the address bar → Site settings → Microphone → Allow, then refresh.');
      return false;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error('Microphone access is not supported. Use HTTPS or a modern browser.');
      return false;
    }
    try {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = getSpeechRecognitionLang();
      voiceFinalRef.current = '';
      voiceInterimRef.current = '';
      recognition.onresult = (event) => {
        let final = '';
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) final += transcript;
          else interim += transcript;
        }
        if (final) {
          voiceFinalRef.current += (voiceFinalRef.current ? ' ' : '') + final.trim();
          voiceInterimRef.current = '';
        } else {
          voiceInterimRef.current = interim.trim();
        }
        const combined = (voiceFinalRef.current + (voiceInterimRef.current ? ' ' + voiceInterimRef.current : '')).trim();
        setReplyText(combined ? combined + ' ' : '');
      };
      recognition.onerror = (event) => {
        if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
          toast.error('Microphone blocked. Allow mic access in your browser and ensure the page is served over HTTPS.');
        } else if (event.error !== 'no-speech' && event.error !== 'aborted') {
          console.error('Speech recognition error:', event.error);
          toast.error(`Voice error: ${event.error}`);
        }
        setVoiceListening(false);
        recognitionRef.current = null;
      };
      recognition.onend = () => {
        if (voiceInterimRef.current) {
          voiceFinalRef.current += (voiceFinalRef.current ? ' ' : '') + voiceInterimRef.current.trim();
          voiceInterimRef.current = '';
          const combined = voiceFinalRef.current.trim();
          setReplyText(combined ? combined + ' ' : '');
        }
        setVoiceListening(false);
        recognitionRef.current = null;
      };
      recognition.start();
      recognitionRef.current = recognition;
      setVoiceListening(true);
      return true;
    } catch (err) {
      console.error('Speech recognition start error:', err);
      toast.error('Could not start voice-to-text');
      return false;
    }
  };

  const stopVoiceToText = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
      setVoiceListening(false);
    }
  };

  const handleComposerSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPhone || configured === false) return;
    let text = replyText.trim();
    if (!text) return;

    if (!isEnglishCode(translateLang) && translatedPreview) {
      text = translatedPreview;
    }

    if (mediaFile) {
      setComposerSending(true);
      try {
        const form = new FormData();
        form.append('phone', selectedPhone);
        form.append('caption', text);
        form.append('file', mediaFile);
        if (waChatLineId) form.append('sender_id', waChatLineId);
        await axios.post('/api/whatsapp/send-media-file', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Sent');
        setReplyText('');
        setMediaFile(null);
        if (attachmentInputRef.current) attachmentInputRef.current.value = '';
        loadMessages(selectedPhone);
        loadConversations();
      } catch (err) {
        toast.error(err.response?.data?.error || err.message || 'Send failed');
      } finally {
        setComposerSending(false);
      }
      return;
    }

    setComposerSending(true);
    try {
      await axios.post('/api/whatsapp/send-text', {
        phone: selectedPhone,
        text,
        ...(waChatLineId ? { sender_id: waChatLineId } : {}),
      });
      toast.success('Message sent');
      setReplyText('');
      loadMessages(selectedPhone);
      loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Send failed');
    } finally {
      setComposerSending(false);
    }
  };

  const handleSendTemplate = async (e) => {
    e.preventDefault();
    setSendingTemplate(true);
    try {
      const urlSuffixTrim = tplUrlButtonSuffix.trim();
      if (tplMode === 'simple' && /^https?:\/\//i.test(urlSuffixTrim)) {
        toast.error(
          'URL button suffix must not be a full URL. For a static button link, leave this empty in the app (set the URL only in Meta). Use this field only for the short dynamic tail after Meta’s base URL.'
        );
        setSendingTemplate(false);
        return;
      }
      const phones = parseTemplatePhoneList(tplPhone, tplBulkPhones);
      if (phones.length === 0) {
        toast.error('Enter at least one phone number (first field and/or additional numbers).');
        setSendingTemplate(false);
        return;
      }
      const simpleBodyParams =
        tplMode === 'simple'
          ? [tplDocText, tplDocText2, tplDocText3].map((s) => String(s || '').trim()).filter(Boolean)
          : [];
      if (tplMode === 'simple' && simpleBodyParams.length > 0) {
        for (let i = 0; i < simpleBodyParams.length; i += 1) {
          const p = simpleBodyParams[i];
          if (p.includes('{{')) {
            toast.error(
              `Body variable ${i + 1} must not contain {{…}} — that is only in Meta’s template. Use only the replacement text (e.g. a name). Utility templates also enforce a 30-character limit per variable on Meta’s side.`
            );
            setSendingTemplate(false);
            return;
          }
        }
        const hp = tplHeaderParam.trim();
        if (hp && hp.includes('{{')) {
          toast.error(
            'Header text variable must not include {{…}}. Use only the replacement for a TEXT header variable, or leave empty if the header is fixed in Meta.'
          );
          setSendingTemplate(false);
          return;
        }
      }
      const bodyTextPreview =
        selectedWhatsappTemplate && selectedTemplateParsed.bodyText
          ? selectedTemplateParsed.bodyText
              .replace(/\{\{1\}\}/g, tplDocText)
              .replace(/\{\{2\}\}/g, tplDocText2)
              .replace(/\{\{3\}\}/g, tplDocText3)
          : tplDocText;
      const headerTextPreview =
        selectedWhatsappTemplate && selectedTemplateParsed.headerText
          ? selectedTemplateParsed.headerText.replace(/\{\{1\}\}/g, tplHeaderParam)
          : '';
      const templatePreview =
        selectedWhatsappTemplate && selectedTemplateParsed
          ? {
              name: selectedWhatsappTemplate.name || tplName,
              headerText: headerTextPreview,
              bodyText: bodyTextPreview,
              footerText: selectedTemplateParsed.footerText,
              buttons: selectedTemplateParsed.urlButtons.map((b) => ({
                text: b.text,
                url: b.hasVariable
                  ? b.url.replace(/\{\{\d+\}\}/g, () => tplUrlButtonSuffix || '[suffix]')
                  : b.url,
              })),
            }
          : undefined;
      const payload = {
        template_mode: tplMode,
        document_link: tplDocLink,
        document_text: tplDocText,
        template_name: tplName || undefined,
        language_code: tplLang || undefined,
        body_text_preview: bodyTextPreview || undefined,
        template_preview: templatePreview,
        ...(tplMode === 'simple' && simpleBodyParams.length ? { body_parameters: simpleBodyParams } : {}),
        ...(tplMode === 'simple' && tplHeaderParam.trim()
          ? { header_text_param: tplHeaderParam.trim() }
          : {}),
        ...(tplMode === 'simple' && urlSuffixTrim && !/^https?:\/\//i.test(urlSuffixTrim)
          ? { url_button_suffix: urlSuffixTrim }
          : {}),
        ...(tplSalespersonPhone.trim() ? { salesperson_phone: tplSalespersonPhone.trim() } : {}),
        ...(tplAlertPhone.trim() ? { alert_phone: tplAlertPhone.trim() } : {}),
      };
      const delayMs = 400;
      let ok = 0;
      let firstErr = null;
      for (let i = 0; i < phones.length; i += 1) {
        const phoneDigits = phones[i];
        try {
          await axios.post('/api/whatsapp/send-template-document', {
            ...payload,
            phone: phoneDigits,
            ...(waTplSenderId ? { sender_id: waTplSenderId } : {}),
          });
          ok += 1;
        } catch (err) {
          if (!firstErr) firstErr = err;
        }
        if (i < phones.length - 1) {
          await new Promise((r) => setTimeout(r, delayMs));
        }
      }
      if (ok === phones.length) {
        toast.success(phones.length > 1 ? `Template sent to ${phones.length} numbers` : 'Template message sent');
        setShowTemplatePanel(false);
        setShowQuickTemplateModal(false);
      } else if (ok > 0) {
        const d = firstErr?.response?.data?.details;
        const detailMsg =
          typeof d === 'string'
            ? d
            : d?.error?.error_data?.details || d?.error_data?.details || d?.error?.message || '';
        const hint0 = firstErr?.response?.data?.hint;
        toast.error(
          `Sent ${ok}/${phones.length}. ${detailMsg || firstErr?.response?.data?.error || firstErr?.message || 'Some sends failed'}${
            hint0 ? `\n\n${hint0}` : ''
          }`
        );
      } else {
        const d = firstErr?.response?.data?.details;
        const detailMsg =
          typeof d === 'string'
            ? d
            : d?.error?.error_data?.details ||
              d?.error_data?.details ||
              d?.error?.message ||
              (d?.message && String(d.message)) ||
              '';
        const main = firstErr?.response?.data?.error || firstErr?.message || 'Send failed';
        const hint1 = firstErr?.response?.data?.hint;
        toast.error(`${detailMsg ? `${main}: ${detailMsg}` : main}${hint1 ? `\n\n${hint1}` : ''}`);
      }
      setSelectedPhone(phones[0]);
      loadConversations();
      loadMessages(phones[0]);
    } catch (err) {
      const d = err.response?.data?.details;
      const detailMsg =
        typeof d === 'string'
          ? d
          : d?.error?.error_data?.details ||
            d?.error_data?.details ||
            d?.error?.message ||
            (d?.message && String(d.message)) ||
            '';
      const main = err.response?.data?.error || err.message || 'Send failed';
      const hint2 = err.response?.data?.hint;
      toast.error(`${detailMsg ? `${main}: ${detailMsg}` : main}${hint2 ? `\n\n${hint2}` : ''}`);
    } finally {
      setSendingTemplate(false);
    }
  };

  // ---------- WhatsApp scheduling helpers ----------

  const loadWaSchedules = useCallback(async () => {
    try {
      setLoadingWaSchedules(true);
      const { data } = await axios.get('/api/whatsapp/schedules', {
        params: { _t: Date.now() },
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      });
      setWaSchedules(data.schedules || []);
    } catch (err) {
      toast.error('Could not load scheduled messages');
      console.error('loadWaSchedules:', err);
    } finally {
      setLoadingWaSchedules(false);
    }
  }, []);

  useEffect(() => {
    if (!showScheduleModal) return;
    loadWaSchedules();
    const interval = setInterval(() => loadWaSchedules(), 10000);
    return () => clearInterval(interval);
  }, [showScheduleModal, loadWaSchedules]);

  const selectedScheduleTemplate = useMemo(
    () => whatsappTemplates.find((t) => t.id === scheduleTemplateId) || null,
    [whatsappTemplates, scheduleTemplateId]
  );

  const selectedScheduleTemplateParsed = useMemo(
    () => parseMetaTemplateComponents(selectedScheduleTemplate?.components),
    [selectedScheduleTemplate]
  );

  const checkScheduleRecipientWindow = useCallback(async (phone) => {
    const p = phone || schedulePhone;
    if (!p || p.length < 8) {
      setScheduleRecipientWindow({ inWindow: false, windowOpenAtScheduledTime: false, lastInbound: null });
      return;
    }
    try {
      const { data } = await axios.get('/api/whatsapp/last-inbound-time', { params: { phone: p } });
      const lastAt = data.last_inbound_at || null;
      const lastAtDate = parseDateTimeLocal(lastAt);
      const inWindow = lastAtDate ? Date.now() - lastAtDate.getTime() < 24 * 60 * 60 * 1000 : false;
      const scheduledDate = parseDateTimeLocal(scheduleAt);
      const windowOpenAtScheduledTime = lastAtDate && scheduledDate
        ? scheduledDate.getTime() - lastAtDate.getTime() < 24 * 60 * 60 * 1000
        : inWindow;
      setScheduleRecipientWindow({ inWindow, windowOpenAtScheduledTime, lastInbound: lastAt });
    } catch {
      setScheduleRecipientWindow({ inWindow: false, windowOpenAtScheduledTime: false, lastInbound: null });
    }
  }, [schedulePhone, scheduleAt]);

  useEffect(() => {
    const timer = setTimeout(() => checkScheduleRecipientWindow(), 800);
    return () => clearTimeout(timer);
  }, [schedulePhone, scheduleAt, checkScheduleRecipientWindow]);

  const openScheduleModal = useCallback((phone = '') => {
    setSchedulePhone(phone || '');
    setScheduleBody('');
    setScheduleAt('');
    setScheduleTemplateId('');
    setScheduleTemplateParams(['', '', '']);
    setScheduleHeaderParam('');
    setScheduleUrlSuffix('');
    setScheduleDocLink('');
    setScheduleRecipientWindow({ inWindow: false, windowOpenAtScheduledTime: false, lastInbound: null });
    setShowScheduleModal(true);
  }, []);

  const handleCreateSchedule = async (e) => {
    e.preventDefault();
    if (!schedulePhone) return toast.error('Phone number is required');
    if (!scheduleBody) return toast.error('Message body is required');
    if (!scheduleAt) return toast.error('Schedule time is required');

    if (!scheduleRecipientWindow.windowOpenAtScheduledTime && !selectedScheduleTemplate) {
      return toast.error(
        'The 24-hour customer service window is closed (or will close before the scheduled time). Please select a Meta-approved template as a fallback.'
      );
    }

    const tpl = selectedScheduleTemplate;
    const parsed = selectedScheduleTemplateParsed;
    const params = scheduleTemplateParams
      .slice(0, parsed.bodyVariableCount || 1)
      .map((s) => String(s || '').trim())
      .filter(Boolean);

    const scheduledDate = parseDateTimeLocal(scheduleAt);
    const payload = {
      name: `Scheduled to ${schedulePhone}`,
      phone_e164: schedulePhone,
      body: scheduleBody,
      schedule_type: 'once',
      scheduled_at: scheduledDate ? scheduledDate.toISOString() : new Date(scheduleAt).toISOString(),
      timezone: scheduleTimezone,
      ...(tpl
        ? {
            template_id: tpl.id,
            template_name: tpl.metaTemplateName,
            template_language: tpl.languageCode,
            template_mode: tpl.templateMode || 'simple',
            template_body_parameters: params.length ? params : [],
            ...(scheduleHeaderParam.trim() ? { template_header_param: scheduleHeaderParam.trim() } : {}),
            ...(scheduleUrlSuffix.trim() ? { template_url_button_suffix: scheduleUrlSuffix.trim() } : {}),
            ...(tpl.templateMode === 'document' && scheduleDocLink.trim()
              ? { template_document_link: scheduleDocLink.trim() }
              : {}),
          }
        : {}),
    };

    try {
      await axios.post('/api/whatsapp/schedules', payload);
      toast.success('WhatsApp message scheduled');
      setScheduleBody('');
      setScheduleAt('');
      setScheduleTemplateId('');
      setScheduleTemplateParams(['', '', '']);
      setScheduleHeaderParam('');
      setScheduleUrlSuffix('');
      setScheduleDocLink('');
      loadWaSchedules();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Schedule failed');
    }
  };

  const handleCancelWaSchedule = async (id) => {
    try {
      await axios.post(`/api/whatsapp/schedules/${id}/cancel`);
      toast.success('Schedule cancelled');
      loadWaSchedules();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Cancel failed');
    }
  };

  const handleDeleteWaSchedule = async (id) => {
    if (!window.confirm('Delete this scheduled message?')) return;
    try {
      await axios.delete(`/api/whatsapp/schedules/${id}`);
      toast.success('Schedule deleted');
      loadWaSchedules();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Delete failed');
    }
  };

  const handleDeleteChat = async () => {
    if (!selectedPhone) return;
    if (
      !window.confirm(
        'Remove this chat from this app’s inbox? This only deletes local history on this server — it does not delete the conversation in WhatsApp on anyone’s phone.'
      )
    ) {
      return;
    }
    setDeletingChat(true);
    try {
      await axios.post('/api/whatsapp/conversation/delete-chat', { phone: selectedPhone });
      toast.success('Chat removed from inbox');
      setSelectedPhone('');
      setMessages([]);
      loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Delete failed');
    } finally {
      setDeletingChat(false);
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm('Delete this message from this app? This will not delete it on WhatsApp.')) return;
    try {
      await axios.delete(`/api/whatsapp/messages/${id}`);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success('Message deleted');
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Delete failed');
    }
  };

  const handleReturnToBot = async () => {
    if (!selectedPhone) return;
    setReturningToBot(true);
    try {
      await axios.post('/api/whatsapp/conversation/return-to-bot', {
        phone: selectedPhone,
        ...(waChatLineId ? { sender_id: waChatLineId } : {}),
      });
      toast.success('Customer returned to bot menu');
      loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Request failed');
    } finally {
      setReturningToBot(false);
    }
  };

  const handleSaveDisplayName = async () => {
    if (!editingDisplayName) return;
    setSavingDisplayName(true);
    try {
      await axios.post('/api/whatsapp/conversation/display-name', {
        phone: editingDisplayName,
        display_name: displayNameInput.trim(),
      });
      toast.success('Display name saved');
      setEditingDisplayName(null);
      setDisplayNameInput('');
      loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSavingDisplayName(false);
    }
  };

  const handleShareChat = async () => {
    if (!selectedPhone || messages.length === 0) return;
    const header = `WhatsApp chat with ${getDisplayLabel(selectedPhone)} (${selectedPhone})\n`;
    const lines = messages.map((m) => {
      const who = m.direction === 'out' ? 'You' : 'Customer';
      const time = fmtLocal(m.created_at);
      const text = m.body || `[${m.variety}]`;
      return `[${time}] ${who}: ${text}`;
    });
    const transcript = header + lines.join('\n');
    try {
      await navigator.clipboard.writeText(transcript);
      toast.success('Chat transcript copied to clipboard');
    } catch {
      toast.error('Could not copy transcript');
    }
  };

  const handleForwardSend = async (toPhone) => {
    if (!forwardMessage || !toPhone) return;
    const text = forwardMessage.body ? `Forwarded:\n${forwardMessage.body}` : '';
    if (!text) {
      toast.error('Cannot forward this message type');
      return;
    }
    try {
      await axios.post('/api/whatsapp/send-text', {
        phone: toPhone,
        text,
        ...(waChatLineId ? { sender_id: waChatLineId } : {}),
      });
      toast.success('Message forwarded');
      setForwardMessage(null);
      if (selectedPhone === toPhone) {
        loadMessages(toPhone);
        loadConversations();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Forward failed');
    }
  };

  return (
    <div className={embedded ? 'h-full w-full overflow-hidden' : 'min-h-screen bg-neutral-50'}>
      {!embedded && <AppHeader stats={stats} configured={configured} />}

      <div className={!embedded ? 'w-full max-w-[1850px] mx-auto px-6 lg:px-10 py-8' : 'h-full w-full overflow-hidden'}>
        {configured === false && (
          <div className="mb-6 p-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 text-sm">
            <p className="font-semibold mb-1">WhatsApp API not configured</p>
            <p>
              Add <code className="bg-amber-100 px-1 rounded">WHATSAPP_ACCESS_TOKEN</code> and{' '}
              <code className="bg-amber-100 px-1 rounded">WHATSAPP_PHONE_NUMBER_ID</code> to your server{' '}
              <code className="bg-amber-100 px-1 rounded">.env</code>, or list several lines with{' '}
              <code className="bg-amber-100 px-1 rounded">WHATSAPP_SENDERS_JSON</code> (see server env comments). For inbound messages, set{' '}
              <code className="bg-amber-100 px-1 rounded">WHATSAPP_VERIFY_TOKEN</code> and{' '}
              <code className="bg-amber-100 px-1 rounded">WHATSAPP_APP_SECRET</code>, then subscribe the webhook URL{' '}
              <code className="bg-amber-100 px-1 rounded">/api/whatsapp/webhook</code> in Meta Developer Console.
            </p>
          </div>
        )}

        {showQuickTemplateModal && (
          <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl border border-neutral-200 shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-1 sticky top-0 bg-white z-10 pb-1">
                <h2 className="text-lg font-bold text-neutral-900">Send template</h2>
                <button
                  type="button"
                  onClick={() => setShowQuickTemplateModal(false)}
                  className="shrink-0 p-1.5 rounded-lg text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 transition-colors"
                  title="Close"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-neutral-500 mb-4">
                Choose a Meta-approved template, review the preview, and send.
              </p>
              <WhatsAppTemplateSelector
                templates={whatsappTemplates}
                selectedTemplateId={selectedWhatsappTemplateId}
                onSelectTemplate={handleSelectWhatsappTemplate}
              />

              {selectedWhatsappTemplate && (
                <>
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-neutral-700 mb-1">To</label>
                    {quickPhoneLocked ? (
                      <div className="w-full px-3 py-2 bg-neutral-100 border border-neutral-200 rounded-lg text-sm text-neutral-700 select-none">
                        {tplPhone}
                      </div>
                    ) : (
                      <input
                        className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                        value={tplPhone}
                        onChange={(e) => setTplPhone(e.target.value)}
                        placeholder="e.g. 918217042063"
                      />
                    )}
                  </div>

                  {tplMode === 'document' && (
                    <div className="mt-4">
                      <label className="block text-sm font-semibold text-neutral-700 mb-1">Document URL (public HTTPS)</label>
                      <input
                        className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                        value={tplDocLink}
                        onChange={(e) => setTplDocLink(e.target.value)}
                        placeholder="https://..."
                        required
                      />
                    </div>
                  )}

                  {(tplMode === 'document' || selectedTemplateParsed.bodyVariableCount >= 1 || !selectedWhatsappTemplate._fromMeta) && (
                    <div className="mt-4">
                      <label className="block text-sm font-semibold text-neutral-700 mb-1">
                        {selectedTemplateParsed.bodyVariableCount > 0 || selectedWhatsappTemplate._fromMeta ? 'Body variable 1' : 'Body text parameter'}
                      </label>
                      <textarea
                        className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm min-h-[72px]"
                        value={tplDocText}
                        onChange={(e) => setTplDocText(e.target.value)}
                        placeholder={selectedTemplateParsed.example?.[0] || 'e.g. John'}
                      />
                    </div>
                  )}

                  {tplMode === 'simple' && (selectedTemplateParsed.bodyVariableCount >= 2 || !selectedWhatsappTemplate._fromMeta) && (
                    <div className="mt-4">
                      <label className="block text-sm font-semibold text-neutral-700 mb-1">
                        Body variable 2 <span className="font-normal text-neutral-500">(optional)</span>
                      </label>
                      <input
                        className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                        value={tplDocText2}
                        onChange={(e) => setTplDocText2(e.target.value)}
                        placeholder={selectedTemplateParsed.example?.[1] || 'Only if template has a second body variable'}
                      />
                    </div>
                  )}

                  {tplMode === 'simple' && selectedTemplateParsed.bodyVariableCount >= 3 && (
                    <div className="mt-4">
                      <label className="block text-sm font-semibold text-neutral-700 mb-1">
                        Body variable 3 <span className="font-normal text-neutral-500">(optional)</span>
                      </label>
                      <input
                        className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                        value={tplDocText3}
                        onChange={(e) => setTplDocText3(e.target.value)}
                        placeholder={selectedTemplateParsed.example?.[2] || 'Only if template has a third body variable'}
                      />
                    </div>
                  )}

                  {tplMode === 'simple' && (selectedTemplateParsed.headerHasVariable || !selectedWhatsappTemplate._fromMeta) && (
                    <div className="mt-4">
                      <label className="block text-sm font-semibold text-neutral-700 mb-1">
                        Header text variable <span className="font-normal text-neutral-500">(optional)</span>
                      </label>
                      <input
                        className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                        value={tplHeaderParam}
                        onChange={(e) => setTplHeaderParam(e.target.value)}
                        placeholder="Only if approved template has a TEXT header {{1}}"
                      />
                    </div>
                  )}

                  {tplMode === 'simple' && (selectedTemplateParsed.hasDynamicUrlButton || !selectedWhatsappTemplate._fromMeta) && (
                    <div className="mt-4">
                      <label className="block text-sm font-semibold text-neutral-700 mb-1">
                        URL button suffix <span className="font-normal text-neutral-500">(optional)</span>
                      </label>
                      <input
                        className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                        value={tplUrlButtonSuffix}
                        onChange={(e) => setTplUrlButtonSuffix(e.target.value)}
                        placeholder="Dynamic tail for first URL button, e.g. token or path segment"
                      />
                    </div>
                  )}

                  <div className="mt-4 rounded-lg border border-neutral-200 bg-[#e9e7e4] p-4 text-sm">
                    <p className="font-semibold text-neutral-900 mb-3">Preview</p>
                    <div className="bg-white rounded-lg p-3 shadow-sm max-w-[260px]">
                      {selectedTemplateParsed.headerText && (
                        <p className="text-[13px] font-semibold text-neutral-900 mb-1">
                          {selectedTemplateParsed.headerText.replace(/\{\{(\d+)\}\}/g, (_, n) => {
                            if (n === '1') return tplHeaderParam || `[${selectedTemplateParsed.headerHasVariable ? 'header' : ''}]`;
                            return `[var ${n}]`;
                          }) || ' '}
                        </p>
                      )}
                      {!selectedTemplateParsed.headerText && selectedTemplateParsed.headerFormat && selectedTemplateParsed.headerFormat !== 'TEXT' && (
                        <p className="text-[11px] text-neutral-500 mb-1 italic">[{selectedTemplateParsed.headerFormat.toLowerCase()} header]</p>
                      )}
                      <p className="text-[13px] text-neutral-800 whitespace-pre-wrap break-words">
                        {selectedTemplateParsed.bodyText
                          .replace(/\{\{1\}\}/g, tplDocText || '[body 1]')
                          .replace(/\{\{2\}\}/g, tplDocText2 || '[body 2]')
                          .replace(/\{\{3\}\}/g, tplDocText3 || '[body 3]')}
                      </p>
                      {selectedTemplateParsed.footerText && (
                        <p className="text-[11px] text-neutral-500 mt-2">{selectedTemplateParsed.footerText}</p>
                      )}
                      {selectedTemplateParsed.urlButtons.length > 0 && (
                        <div className="mt-3 space-y-1.5">
                          {selectedTemplateParsed.urlButtons.map((b, idx) => (
                            <div key={idx} className="text-emerald-700 text-[13px] font-medium flex items-center gap-1">
                              <span>↗</span>
                              <span>
                                {b.text}
                                {b.hasVariable && (
                                  <span className="text-neutral-500 font-normal"> — {b.url.replace(/\{\{\d+\}\}/g, () => tplUrlButtonSuffix || '[suffix]')}</span>
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <form onSubmit={handleSendTemplate} className="mt-4">
                    <button
                      type="submit"
                      disabled={sendingTemplate || configured === false || !tplPhone.trim() || !tplName.trim()}
                      className="w-full py-2.5 bg-emerald-600 text-white rounded-lg font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <FiSend className="w-4 h-4" />
                      {sendingTemplate ? 'Sending…' : 'Send template'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        )}

        {showScheduleModal && (
          <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl border border-neutral-200 shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-1 sticky top-0 bg-white z-10 pb-1">
                <h2 className="text-lg font-bold text-neutral-900">Schedule WhatsApp message</h2>
                <button
                  type="button"
                  onClick={() => setShowScheduleModal(false)}
                  className="shrink-0 p-1.5 rounded-lg text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 transition-colors"
                  title="Close"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-neutral-500 mb-4">
                At the scheduled time we check the 24-hour customer service window. If the window is open, your free-form message is sent; otherwise the chosen Meta-approved template is sent as a fallback.
              </p>

              <form onSubmit={handleCreateSchedule} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-1">Phone number</label>
                  <input
                    type="text"
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={schedulePhone}
                    onChange={(e) => setSchedulePhone(e.target.value)}
                    placeholder="e.g. 918217042063"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-1">Message body</label>
                  <textarea
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm min-h-[80px]"
                    value={scheduleBody}
                    onChange={(e) => setScheduleBody(e.target.value)}
                    placeholder="Free-form text sent when the 24h window is open"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-1">Schedule for</label>
                  <input
                    type="datetime-local"
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={scheduleAt}
                    onChange={(e) => setScheduleAt(e.target.value)}
                    required
                  />
                </div>

                <div className="flex items-center gap-2 text-xs">
                  <span className="font-medium text-neutral-600">Window at scheduled time:</span>
                  {scheduleRecipientWindow.windowOpenAtScheduledTime ? (
                    <span className="text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">Inside 24h window — custom message allowed</span>
                  ) : (
                    <span className="text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">Outside 24h window — Meta template fallback required</span>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-1">Fallback Meta template</label>
                  <select
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={scheduleTemplateId}
                    onChange={(e) => {
                      setScheduleTemplateId(e.target.value);
                      setScheduleTemplateParams(['', '', '']);
                      setScheduleHeaderParam('');
                      setScheduleUrlSuffix('');
                      setScheduleDocLink('');
                    }}
                  >
                    <option value="">-- No template (fails if outside 24h window) --</option>
                    {whatsappTemplates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.templateMode || 'simple'})
                      </option>
                    ))}
                  </select>
                </div>

                {selectedScheduleTemplate && (
                  <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 space-y-3">
                    {selectedScheduleTemplateParsed.bodyVariableCount >= 1 && (
                      <div>
                        <label className="block text-xs font-semibold text-neutral-700 mb-1">Body variable 1</label>
                        <input
                          type="text"
                          className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                          value={scheduleTemplateParams[0]}
                          onChange={(e) =>
                            setScheduleTemplateParams((prev) => {
                              const next = [...prev];
                              next[0] = e.target.value;
                              return next;
                            })
                          }
                          placeholder={selectedScheduleTemplateParsed.example?.[0] || 'Value for {{1}}'}
                        />
                      </div>
                    )}
                    {selectedScheduleTemplateParsed.bodyVariableCount >= 2 && (
                      <div>
                        <label className="block text-xs font-semibold text-neutral-700 mb-1">Body variable 2</label>
                        <input
                          type="text"
                          className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                          value={scheduleTemplateParams[1]}
                          onChange={(e) =>
                            setScheduleTemplateParams((prev) => {
                              const next = [...prev];
                              next[1] = e.target.value;
                              return next;
                            })
                          }
                          placeholder={selectedScheduleTemplateParsed.example?.[1] || 'Value for {{2}}'}
                        />
                      </div>
                    )}
                    {selectedScheduleTemplateParsed.bodyVariableCount >= 3 && (
                      <div>
                        <label className="block text-xs font-semibold text-neutral-700 mb-1">Body variable 3</label>
                        <input
                          type="text"
                          className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                          value={scheduleTemplateParams[2]}
                          onChange={(e) =>
                            setScheduleTemplateParams((prev) => {
                              const next = [...prev];
                              next[2] = e.target.value;
                              return next;
                            })
                          }
                          placeholder={selectedScheduleTemplateParsed.example?.[2] || 'Value for {{3}}'}
                        />
                      </div>
                    )}
                    {selectedScheduleTemplateParsed.headerHasVariable && (
                      <div>
                        <label className="block text-xs font-semibold text-neutral-700 mb-1">Header text variable</label>
                        <input
                          type="text"
                          className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                          value={scheduleHeaderParam}
                          onChange={(e) => setScheduleHeaderParam(e.target.value)}
                          placeholder="Value for TEXT header {{1}}"
                        />
                      </div>
                    )}
                    {selectedScheduleTemplateParsed.hasDynamicUrlButton && (
                      <div>
                        <label className="block text-xs font-semibold text-neutral-700 mb-1">URL button suffix</label>
                        <input
                          type="text"
                          className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                          value={scheduleUrlSuffix}
                          onChange={(e) => setScheduleUrlSuffix(e.target.value)}
                          placeholder="Dynamic tail after Meta base URL"
                        />
                      </div>
                    )}
                    {selectedScheduleTemplate.templateMode === 'document' && (
                      <div>
                        <label className="block text-xs font-semibold text-neutral-700 mb-1">Document URL</label>
                        <input
                          type="url"
                          className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                          value={scheduleDocLink}
                          onChange={(e) => setScheduleDocLink(e.target.value)}
                          placeholder="https://..."
                          required
                        />
                      </div>
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!scheduleRecipientWindow.windowOpenAtScheduledTime && !selectedScheduleTemplate}
                  title={
                    !scheduleRecipientWindow.windowOpenAtScheduledTime && !selectedScheduleTemplate
                      ? 'The 24-hour window is closed (or will close before the scheduled time). Select a Meta template as a fallback to schedule.'
                      : ''
                  }
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors disabled:bg-neutral-400 disabled:cursor-not-allowed"
                >
                  <FiClock className="w-4 h-4" />
                  {!scheduleRecipientWindow.windowOpenAtScheduledTime && !selectedScheduleTemplate
                    ? 'Select a template to schedule'
                    : 'Schedule message'}
                </button>
              </form>

              <div className="mt-6 border-t border-neutral-200 pt-4">
                <h3 className="text-sm font-bold text-neutral-900 mb-2">Scheduled messages</h3>
                {loadingWaSchedules ? (
                  <p className="text-xs text-neutral-500">Loading...</p>
                ) : waSchedules.length === 0 ? (
                  <p className="text-xs text-neutral-500">No scheduled WhatsApp messages.</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {waSchedules.map((s) => (
                      <div
                        key={s.id}
                        className="flex items-center justify-between gap-2 p-2 rounded-lg border border-neutral-200 bg-neutral-50 text-xs"
                      >
                        <div className="min-w-0">
                          <p className="font-medium text-neutral-900 truncate">{s.phone_e164}</p>
                          <p className="text-neutral-500 truncate">{s.body}</p>
                          <p className="text-neutral-400">
                            {s.scheduled_at ? new Date(s.scheduled_at).toLocaleString() : 'Recurring'}
                            {' · '}
                            <span className={s.status === 'pending' ? 'text-amber-600' : s.status === 'completed' ? 'text-emerald-600' : 'text-red-600'}>
                              {s.status}
                            </span>
                            {s.template_name && (
                              <span className="text-sky-600 ml-1">fallback: {s.template_name}</span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {s.status === 'pending' && (
                            <button
                              type="button"
                              onClick={() => handleCancelWaSchedule(s.id)}
                              className="p-1.5 rounded-md text-neutral-500 hover:bg-neutral-200"
                              title="Cancel"
                            >
                              <FiX className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleDeleteWaSchedule(s.id)}
                            className="p-1.5 rounded-md text-red-500 hover:bg-red-50"
                            title="Delete"
                          >
                            <FiTrash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {showTemplatePanel && (
          <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl border border-neutral-200 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-1 sticky top-0 bg-white z-10 pb-1">
                <h2 className="text-lg font-bold text-neutral-900">Send template</h2>
                <button
                  type="button"
                  onClick={() => setShowTemplatePanel(false)}
                  className="shrink-0 p-1.5 rounded-lg text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 transition-colors"
                  title="Close"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>
            <p className="text-xs text-neutral-500 mb-4">
              Template must be approved in Meta Business Manager. Phone: country code + number, digits only or with
              formatting.
            </p>
            <WhatsAppTemplateSelector
              templates={whatsappTemplates}
              selectedTemplateId={selectedWhatsappTemplateId}
              onSelectTemplate={handleSelectWhatsappTemplate}
            />
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  setTemplateToEdit(null);
                  setShowWhatsappTemplateEditor(true);
                }}
                className="px-4 py-2 bg-white border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 text-sm font-semibold"
              >
                New template
              </button>
              <button
                type="button"
                disabled={!selectedWhatsappTemplate}
                onClick={() => {
                  setTemplateToEdit(selectedWhatsappTemplate);
                  setShowWhatsappTemplateEditor(true);
                }}
                className="px-4 py-2 bg-white border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Edit selected
              </button>
            </div>

            {selectedWhatsappTemplate && (
              <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 text-sm">
                <p className="font-semibold text-emerald-900 mb-2">Preview</p>
                <div className="space-y-1 text-emerald-900">
                  <p><span className="text-neutral-600">Template:</span> {selectedWhatsappTemplate.name || tplName}</p>
                  <p><span className="text-neutral-600">Language:</span> {tplLang || 'en_US'}</p>
                  <p><span className="text-neutral-600">Mode:</span> {tplMode === 'document' ? 'Document' : 'Simple text'}</p>
                  {tplMode === 'document' && tplDocLink && (
                    <p className="truncate"><span className="text-neutral-600">Document:</span> {tplDocLink}</p>
                  )}
                  {(tplDocText || tplDocText2 || tplDocText3) && (
                    <p><span className="text-neutral-600">Body:</span> {tplDocText}{tplDocText2 ? ` · ${tplDocText2}` : ''}{tplDocText3 ? ` · ${tplDocText3}` : ''}</p>
                  )}
                  {tplHeaderParam && <p><span className="text-neutral-600">Header:</span> {tplHeaderParam}</p>}
                  {tplUrlButtonSuffix && <p><span className="text-neutral-600">URL suffix:</span> {tplUrlButtonSuffix}</p>}
                </div>
              </div>
            )}

            <form onSubmit={handleSendTemplate} className="space-y-4">
              {configured && waSenders.length > 0 && (
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-1">
                    Send this template from <span className="font-normal text-neutral-500">(choose line)</span>
                  </label>
                  <select
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={waTplSenderId}
                    onChange={(e) => persistWaTplSenderId(e.target.value)}
                  >
                    {waSenders.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label} — {s.phone_number_id}
                      </option>
                    ))}
                  </select>
                  {waSenders.length > 1 && (
                    <p className="text-xs text-neutral-500 mt-1">
                      Chats are filtered by the <strong>Inbox</strong> switch in the Chats panel. This only affects
                      template sends.
                    </p>
                  )}
                </div>
              )}
              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-1">Customer phone</label>
                <input
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                  value={tplPhone}
                  onChange={(e) => setTplPhone(e.target.value)}
                  placeholder="e.g. 918217042063 (or leave blank if all numbers are below)"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-1">
                  Additional numbers <span className="font-normal text-neutral-500">(optional)</span>
                </label>
                <textarea
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm min-h-[72px]"
                  value={tplBulkPhones}
                  onChange={(e) => setTplBulkPhones(e.target.value)}
                  placeholder="One per line or comma-separated — same template is sent to every number (small delay between sends)."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-1">Template name</label>
                  <input
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={tplName}
                    onChange={(e) => setTplName(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-1">Language code</label>
                  <input
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={tplLang}
                    onChange={(e) => setTplLang(e.target.value)}
                    placeholder="en_US"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-1">Template mode</label>
                <select
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                  value={tplMode}
                  onChange={(e) => setTplMode(e.target.value)}
                >
                  <option value="document">Document template (header media)</option>
                  <option value="simple">Simple text template</option>
                </select>
              </div>
              {tplMode === 'document' && (
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-1">Document URL (public HTTPS)</label>
                  <input
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={tplDocLink}
                    onChange={(e) => setTplDocLink(e.target.value)}
                    placeholder="https://..."
                    required
                  />
                </div>
              )}
              {tplMode === 'simple' && (
                <div className="rounded-lg border border-emerald-100 bg-emerald-50/80 px-3 py-2 text-xs text-emerald-900 space-y-1">
                  <p className="font-semibold">Simple / utility templates</p>
                  <p className="text-amber-800 font-medium">
                    ⚠️ Check your template in Meta Business Manager to see exactly how many {'{{1}}'}, {'{{2}}'}, etc. placeholders it has. The count must match exactly.
                  </p>
                  <p>
                    <strong>No body variables</strong> (e.g. <code className="bg-white/80 px-1 rounded">test_template</code>): leave{' '}
                    <strong>Body variable 1</strong> and <strong>2</strong> empty; set template name + language exactly as in Meta (English is often{' '}
                    <code className="bg-white/80 px-1 rounded">en</code>, not <code className="bg-white/80 px-1 rounded">en_US</code>).
                  </p>
                  <p>
                    <strong>One variable</strong>: template name + language match Meta; fill{' '}
                    <strong>Body variable 1</strong> only (e.g. <em>John</em> for <code className="bg-white/80 px-1 rounded">{'{{1}}'}</code>).
                  </p>
                  <p>
                    <strong>Two variables</strong> (e.g. <code className="bg-white/80 px-1 rounded">account_creation_confirmation_3</code>):{' '}
                    variable 1 = text after “Hi ” (often the name); variable 2 = text in “Please verify …” (often email or short phrase). Order must match Meta{' '}
                    <code className="bg-white/80 px-1 rounded">{'{{1}}'}</code> then <code className="bg-white/80 px-1 rounded">{'{{2}}'}</code>.
                  </p>
                  <p>
                    If the <strong>Verify account</strong> URL has a dynamic tail in Meta, use <strong>URL button suffix</strong> only (not the full URL).
                  </p>
                  <p className="text-amber-900 font-medium border-t border-emerald-200 pt-2 mt-1">
                    <strong>Utility</strong> templates (e.g. account updates): Meta allows at most{' '}
                    <strong>30 characters</strong> per body variable. Do <strong>not</strong> paste the full message or
                    literal <code className="bg-white/80 px-1 rounded">{'{{1}}'}</code> text — only the short values
                    that replace each placeholder (e.g. <em>John</em>, <em>you@site.com</em>).
                  </p>
                  <p className="text-amber-900 border-t border-emerald-200 pt-2 mt-1">
                    If the terminal shows <code className="bg-white/80 px-1 rounded">132000</code> and “expected number
                    of params <strong>(2)</strong>”: your template has <strong>two</strong> body placeholders — fill{' '}
                    <strong>Body variable 1</strong> and <strong>Body variable 2</strong> (each short, e.g. name then
                    email). Leave <strong>Header text variable</strong> empty unless Meta shows a variable in the
                    header. Leave <strong>URL button suffix</strong> empty when the button URL is <strong>static</strong>{' '}
                    in Meta (do not paste <code className="bg-white/80 px-1 rounded">https://…</code> here).
                  </p>
                </div>
              )}
              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-1">
                  {tplMode === 'simple' ? 'Body variable 1' : 'Body text parameter'}
                </label>
                <textarea
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm min-h-[88px]"
                  value={tplDocText}
                  onChange={(e) => setTplDocText(e.target.value)}
                  placeholder={
                    tplMode === 'simple'
                      ? '{{1}} e.g. John — or only field if template has one body variable'
                      : 'Text for the template body variable'
                  }
                />
              </div>
              {tplMode === 'simple' && (
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-1">
                    Body variable 2 <span className="font-normal text-neutral-500">(optional)</span>
                  </label>
                  <input
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={tplDocText2}
                    onChange={(e) => setTplDocText2(e.target.value)}
                    placeholder="{{2}} e.g. name@example.com — only if template has a second body variable"
                  />
                </div>
              )}
              {tplMode === 'simple' && (
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-1">
                    Body variable 3 <span className="font-normal text-neutral-500">(optional)</span>
                  </label>
                  <input
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={tplDocText3}
                    onChange={(e) => setTplDocText3(e.target.value)}
                    placeholder="{{3}} e.g. token or code — only if template has a third body variable"
                  />
                </div>
              )}
              {tplMode === 'simple' && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-neutral-700 mb-1">
                      Header text variable <span className="font-normal text-neutral-500">(optional)</span>
                    </label>
                    <input
                      className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                      value={tplHeaderParam}
                      onChange={(e) => setTplHeaderParam(e.target.value)}
                      placeholder="Only if approved template has a TEXT header {{1}}"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-neutral-700 mb-1">
                      URL button suffix <span className="font-normal text-neutral-500">(optional)</span>
                    </label>
                    <input
                      className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                      value={tplUrlButtonSuffix}
                      onChange={(e) => setTplUrlButtonSuffix(e.target.value)}
                      placeholder="Dynamic tail for first URL button, e.g. token or path segment"
                    />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-1">
                  Salesperson WhatsApp <span className="font-normal text-neutral-500">(optional)</span>
                </label>
                <input
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                  value={tplSalespersonPhone}
                  onChange={(e) => setTplSalespersonPhone(e.target.value)}
                  placeholder="+91... to notify when the recipient replies. Fallback: WHATSAPP_SALES_NOTIFICATION_PHONE"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-1">
                  Alert phone for delivery failures <span className="font-normal text-neutral-500">(optional)</span>
                </label>
                <input
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                  value={tplAlertPhone}
                  onChange={(e) => setTplAlertPhone(e.target.value)}
                  placeholder="+91... to notify if this template is blocked or fails to deliver"
                />
              </div>
              <div className="flex gap-2 items-center">
                <button
                  type="button"
                  onClick={() => setShowAIWhatsAppHelper(true)}
                  disabled={configured === false || !tplDocText.trim() || tplMode !== 'document'}
                  className="px-4 py-2 bg-white border border-neutral-300 text-neutral-700 rounded-lg text-sm font-semibold hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <FiCpu className="w-4 h-4" />
                  AI Assist
                </button>
                <button
                  type="submit"
                  disabled={sendingTemplate || configured === false}
                  className="flex-1 py-2.5 bg-emerald-600 text-white rounded-lg font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <FiSend className="w-4 h-4" />
                  {sendingTemplate ? 'Sending…' : 'Send template'}
                </button>
              </div>
            </form>
            <WhatsAppMetaTemplateManager senderId={waTplSenderId} onSync={loadWhatsappTemplates} />
            </div>
          </div>
        )}

        <div className={embedded ? 'h-full' : 'grid grid-cols-1 gap-6 items-start'}>
            <div className={isFullscreen
              ? 'fixed inset-0 z-50 bg-white flex flex-col shadow-2xl'
              : embedded
                ? 'bg-white h-full flex flex-col overflow-hidden'
                : 'bg-white rounded-2xl border border-neutral-200 shadow-soft overflow-hidden flex flex-col h-[74vh] lg:h-[calc(100vh-150px)] min-h-[560px]'
            }>
            <div className="px-3 py-1.5 border-b border-neutral-200 flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h2 className="text-base font-bold text-neutral-900">Chats</h2>
                <p className="text-[10px] text-neutral-500 mt-0.5 leading-tight truncate">
                  Bot menu + agent handoff · 24h session window
                  {autoReplyInfo.enabled ? ` · menu cooldown ~${autoReplyInfo.cooldown}s` : ' · auto-reply off'}
                </p>
                {configured && waSenders.length > 1 && (
                  <div className="mt-2 flex items-center gap-2 min-w-0">
                    <label htmlFor="wa-inbox-line" className="text-[10px] font-semibold text-neutral-600 shrink-0">
                      Inbox
                    </label>
                    <select
                      id="wa-inbox-line"
                      className="flex-1 min-w-0 border border-neutral-200 rounded-md px-2 py-1 text-[11px] bg-white"
                      value={waChatLineId}
                      onChange={(e) => persistWaChatLineId(e.target.value)}
                      title="Show only threads for this WhatsApp Business number"
                    >
                      {waSenders.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.label} — {s.phone_number_id}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1">
                <LanguageSelector
                  value={translateLang}
                  onChange={handleTranslateLangChange}
                  title="Translate incoming messages and outgoing replies"
                />
                <button
                  type="button"
                  onClick={() => openQuickTemplateModal()}
                  className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition-colors"
                  title="Start a new chat with a template"
                >
                  <FiPlus className="w-3.5 h-3.5" /> New chat
                </button>
                <button
                  type="button"
                  onClick={() => openScheduleModal(selectedPhone)}
                  className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-sky-600 text-white text-xs font-semibold hover:bg-sky-700 transition-colors"
                  title="Schedule a WhatsApp message"
                >
                  <FiClock className="w-3.5 h-3.5" /> Schedule
                </button>
                <button
                  type="button"
                  onClick={() => openTemplatePanel()}
                  className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white border border-neutral-200 text-neutral-700 text-xs font-semibold hover:bg-neutral-50 transition-colors"
                  title="Broadcast template to multiple numbers"
                >
                  <FiSend className="w-3.5 h-3.5" /> Broadcast
                </button>
                <button
                  type="button"
                  onClick={() => setIsFullscreen((v) => !v)}
                  className="shrink-0 p-1.5 rounded-lg text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 transition-colors"
                  title={isFullscreen ? 'Exit full screen' : 'Full screen'}
                >
                  {isFullscreen ? <FiMinimize2 className="w-4 h-4" /> : <FiMaximize2 className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex flex-1 min-h-0 flex-col md:flex-row">
              <div className="md:w-44 md:h-full border-b md:border-b-0 md:border-r border-neutral-200 max-h-40 md:max-h-none overflow-y-auto shrink-0">
                <div className="p-2 border-b border-neutral-200 bg-white space-y-1.5 sticky top-0 z-10">
                  <input
                    className="w-full border border-neutral-200 rounded-md px-2 py-1.5 text-xs"
                    placeholder="Search number or name…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                {loadingList && filteredConversations.length === 0 ? (
                  <p className="p-2 text-xs text-neutral-400">Loading…</p>
                ) : filteredConversations.length === 0 ? (
                  <p className="p-2 text-xs text-neutral-500">No threads yet</p>
                ) : (
                  filteredConversations.map((c) => {
                    const win = getSessionWindow(c.last_inbound_at);
                    const dotColor =
                      win.urgency === 'ok' ? 'bg-emerald-400' :
                      win.urgency === 'warn' ? 'bg-amber-400' : 'bg-neutral-300';
                    return (
                    <div
                      key={c.phone_e164}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedPhone(c.phone_e164)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedPhone(c.phone_e164); }}}
                      className={`w-full text-left px-2 py-1.5 text-xs border-b border-neutral-100 hover:bg-neutral-50 leading-tight cursor-pointer ${
                        selectedPhone === c.phone_e164 ? 'bg-emerald-50 text-emerald-900 font-medium' : ''
                      }`}
                      title={`${getDisplayLabel(c.phone_e164)} · Session window: ${win.label}`}
                    >
                      <div className="flex items-center gap-1 truncate group">
                        <span
                          className={`shrink-0 w-2 h-2 rounded-full ${dotColor}`}
                          title={win.label}
                        />
                        <span className="truncate font-medium">{getDisplayLabel(c.phone_e164)}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingDisplayName(c.phone_e164);
                            setDisplayNameInput(getContactName(c.phone_e164));
                          }}
                          className="opacity-0 group-hover:opacity-100 p-0.5 text-neutral-400 hover:text-emerald-600 transition-opacity"
                          title="Edit name"
                        >
                          <FiEdit2 className="w-3 h-3" />
                        </button>
                        {Number(c.agent_mode) === 1 && (
                          <span
                            className="inline-flex items-center gap-0.5 shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-violet-100 text-violet-800"
                            title="Waiting for agent"
                          >
                            <FiUser className="w-3 h-3" />
                            Live
                          </span>
                        )}
                      </div>
                      <div className="text-[9px] text-neutral-400 truncate mt-0.5 flex items-center gap-1">
                        {getContactName(c.phone_e164) ? (
                          <>
                            <span className="font-mono text-neutral-500">{c.phone_e164}</span>
                            <span>·</span>
                          </>
                        ) : null}
                        <span>{fmtLocal(c.last_at)}</span>
                      </div>
                      <div className={`text-[9px] mt-0.5 font-medium truncate ${
                        win.urgency === 'ok' ? 'text-emerald-600' :
                        win.urgency === 'warn' ? 'text-amber-600' : 'text-neutral-400'
                      }`}>
                        {win.label}
                      </div>
                    </div>
                    );
                  })
                )}
              </div>
              <div className="flex-1 flex flex-col min-h-[280px] relative min-w-0">
                {selectedPhone && (() => {
                  const win = getSessionWindow(selectedConv?.last_inbound_at);
                  const lastInboundLocal = fmtLocal(selectedConv?.last_inbound_at);
                  const expiryLocal = selectedConv?.last_inbound_at
                    ? fmtLocal(new Date(parseUtcTimestamp(selectedConv.last_inbound_at).getTime() + SESSION_WINDOW_MS).toISOString())
                    : null;
                  if (win.urgency === 'expired') {
                    return (
                      <div className="px-2 py-1.5 bg-neutral-100 border-b border-neutral-200 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-neutral-400 shrink-0" />
                        <p className="text-[11px] text-neutral-600 leading-snug">
                          <span className="font-semibold">Session expired</span>
                          {lastInboundLocal ? ` — last customer message: ${lastInboundLocal}.` : '.'}
                          {' '}Use a <span className="font-semibold">template</span> to re-open the window.
                        </p>
                      </div>
                    );
                  }
                  if (win.urgency === 'warn') {
                    return (
                      <div className="px-2 py-1.5 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
                        <p className="text-[11px] text-amber-800 leading-snug">
                          <span className="font-semibold">Session closing soon</span> — {win.label}
                          {expiryLocal ? ` (expires ${expiryLocal})` : ''}.
                          Customer must reply to extend.
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div className="px-2 py-1 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
                      <p className="text-[11px] text-emerald-800 leading-snug">
                        Session open · {win.label}
                        {expiryLocal ? ` · expires ${expiryLocal}` : ''}
                      </p>
                    </div>
                  );
                })()}
                {selectedPhone && (
                  <div className="px-2 py-1 border-b border-neutral-200 bg-white flex flex-wrap items-center justify-between gap-2">
                    <p className="text-[10px] text-neutral-500 leading-snug">
                      Thread with{' '}
                      <span className="font-semibold text-neutral-900">
                        {getDisplayLabel(selectedPhone)}
                      </span>
                      {getContactName(selectedPhone) ? (
                        <span className="font-mono text-neutral-700"> ({selectedPhone})</span>
                      ) : null}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleShareChat}
                        disabled={messages.length === 0}
                        className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg border border-neutral-200 text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
                      >
                        <FiShare2 className="w-3.5 h-3.5" />
                        Share chat
                      </button>
                      <button
                        type="button"
                        onClick={handleDeleteChat}
                        disabled={deletingChat || configured === false}
                        className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        <FiTrash2 className="w-3.5 h-3.5" />
                        {deletingChat ? '…' : 'Delete chat'}
                      </button>
                    </div>
                  </div>
                )}
                {selectedPhone && waSenders.length > 1 && (
                  <div className="relative px-2 py-1 border-b border-neutral-200 bg-neutral-50/80 flex items-center justify-between gap-2 min-h-[2.25rem]">
                    <label className="flex items-center gap-2 cursor-pointer select-none text-[11px] text-neutral-800 min-w-0">
                      <input
                        type="checkbox"
                        className="shrink-0 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-500"
                        checked={showAllLinesInOpenChat}
                        onChange={(e) => setShowAllLinesInOpenChat(e.target.checked)}
                      />
                      <span className="leading-tight">
                        <span className="font-semibold">Show all lines</span>
                        <span className="text-neutral-500 font-normal"> — merged thread for this contact</span>
                      </span>
                    </label>
                    <div className="relative shrink-0">
                      <button
                        type="button"
                        className="inline-flex items-center justify-center p-1.5 rounded-lg text-sky-700 bg-white border border-sky-200/80 hover:bg-sky-50 hover:border-sky-300 transition-colors"
                        aria-label="Why multiple lines?"
                        aria-expanded={mergeLinesHelpOpen}
                        aria-haspopup="dialog"
                        onClick={() => setMergeLinesHelpOpen((v) => !v)}
                        title="Why multiple lines?"
                      >
                        <FiInfo className="w-4 h-4" />
                      </button>
                      {mergeLinesHelpOpen && (
                        <>
                          <button
                            type="button"
                            className="fixed inset-0 z-[60] cursor-default bg-transparent"
                            aria-label="Close"
                            onClick={() => setMergeLinesHelpOpen(false)}
                          />
                          <div
                            role="dialog"
                            aria-label="Multiple WhatsApp numbers"
                            className="absolute right-0 top-[calc(100%+6px)] z-[70] w-[min(92vw,20rem)] rounded-xl border border-neutral-200 bg-white shadow-lg p-3 text-[11px] text-neutral-700 leading-relaxed space-y-2"
                          >
                            <p className="font-semibold text-neutral-900 text-xs">Multiple business numbers</p>
                            <p>
                              On the customer&apos;s phone, each of your WhatsApp numbers is a{' '}
                              <strong>separate chat</strong>. This screen still lists one row per customer number and can
                              merge both chats here when needed.
                            </p>
                            <p>
                              The <strong>Inbox</strong> dropdown filters which business line you are working in. Turn on{' '}
                              <strong>Show all lines</strong> to see messages from every configured line for this contact.
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
                {selectedPhone && isAgentHandoff && (
                  <div className="px-2 py-1.5 bg-violet-50 border-b border-violet-100 flex flex-wrap items-center justify-between gap-2">
                    <p className="text-[11px] text-violet-900 leading-snug">
                      <span className="font-semibold">Live agent chat</span> — customer chose &quot;Chat with agent&quot;.
                      They will not see the bot menu until you end the handoff.
                    </p>
                    <button
                      type="button"
                      onClick={handleReturnToBot}
                      disabled={returningToBot || configured === false}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-white border border-violet-200 text-violet-800 hover:bg-violet-100 disabled:opacity-50"
                    >
                      {returningToBot ? 'Updating…' : 'End handoff / back to bot'}
                    </button>
                  </div>
                )}
                <div
                  ref={chatScrollRef}
                  onScroll={handleChatScroll}
                  className="flex-1 overflow-y-auto px-2 py-2 sm:px-3 space-y-1.5 relative"
                  style={{ backgroundColor: '#eef0f2', backgroundImage: CHAT_DOODLE_BG, backgroundSize: '200px 200px' }}
                >
                  {!selectedPhone ? (
                    <p className="text-sm text-neutral-500 text-center py-8">Select a conversation</p>
                  ) : (
                    filteredMessages.map((m) => {
                      const bubbleExtra = parseExtraJson(m.extra_json);
                      const pidNorm =
                        bubbleExtra?.phone_number_id != null
                          ? String(bubbleExtra.phone_number_id).replace(/\D/g, '')
                          : '';
                      const lineLabel =
                        waSenders.length > 1 && pidNorm
                          ? waSenders.find((s) => String(s.phone_number_id).replace(/\D/g, '') === pidNorm)?.label ||
                            pidNorm.slice(-6)
                          : '';
                      const deliveryFailHint =
                        m.direction === 'out' &&
                        normalizeDeliveryStatus(m.delivery_status) === 'failed'
                          ? formatMetaDeliveryFailureTitle(bubbleExtra)
                          : '';
                      const blocked = isBlockedFailure(bubbleExtra);
                      const mediaCaption =
                        bubbleExtra?.caption && String(bubbleExtra.caption).trim();
                      const isMediaKind = ['image', 'document', 'video', 'audio'].includes(m.variety);
                      return (
                      <div
                        key={m.id}
                        className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${
                          m.direction === 'out'
                            ? 'ml-auto bg-emerald-600 text-white'
                            : 'mr-auto bg-white border border-neutral-200 text-neutral-800'
                        }`}
                      >
                        <div className="text-[10px] opacity-80 mb-0.5">
                          {m.direction === 'out' ? 'You' : 'Customer'} ·{' '}
                          {bubbleExtra?.template ? `template · ${bubbleExtra.template}` : m.variety}
                          {lineLabel ? ` · ${lineLabel}` : ''}
                        </div>
                        {mediaCaption && isMediaKind && (
                          <div className="whitespace-pre-wrap break-words text-[13px] leading-snug mb-1.5 opacity-95">
                            {mediaCaption}
                          </div>
                        )}
                        {(() => {
                          const media = getMediaRenderData(m);
                          if (media.type === 'image') {
                            if (failedMedia.has(media.url)) {
                              return (
                                <div className="flex items-center gap-2 px-3 py-2 bg-neutral-100 rounded-lg border border-neutral-200 text-neutral-500 text-xs">
                                  <span>🖼️</span>
                                  <span>Image unavailable — media expired on Meta's servers</span>
                                </div>
                              );
                            }
                            return (
                              <div className="space-y-1 relative group">
                                <img
                                  src={media.url}
                                  alt={media.label || 'Image'}
                                  className="max-h-56 rounded-lg border border-neutral-200 object-cover bg-white cursor-zoom-in"
                                  loading="lazy"
                                  onClick={() => setLightboxImg(media.url)}
                                  onError={() => setFailedMedia((prev) => new Set([...prev, media.url]))}
                                />
                                <button
                                  type="button"
                                  onClick={() => setLightboxImg(media.url)}
                                  className="absolute top-1 right-1 p-1 bg-black/40 hover:bg-black/60 rounded-md text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="View full size"
                                >
                                  <FiZoomIn className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            );
                          }
                          if (media.type === 'document') {
                            return (
                              <a
                                href={media.url}
                                target="_blank"
                                rel="noreferrer"
                                className="underline underline-offset-2 break-all"
                              >
                                📄 {media.label || 'Open document'}
                              </a>
                            );
                          }
                          if (media.type === 'video') {
                            return (
                              <video
                                src={media.url}
                                controls
                                className="max-h-56 max-w-full rounded-lg border border-neutral-200 bg-black"
                              />
                            );
                          }
                          if (media.type === 'audio') {
                            const result = transcriptionResults[m.id];
                            const sourceLang = transcribeSourceLang[m.id] || 'eng_Latn';
                            return (
                              <div className="space-y-2">
                                <audio src={media.url} controls className="max-w-full h-9" />
                                <div className="flex flex-wrap items-center gap-2">
                                  <LanguageSelector
                                    value={sourceLang}
                                    onChange={(code) =>
                                      setTranscribeSourceLang((prev) => ({ ...prev, [m.id]: code }))
                                    }
                                    title="Language of this voice note"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleTranscribeAudio(m.id, media.url, sourceLang)}
                                    disabled={transcribingId === m.id}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 text-xs font-semibold hover:bg-emerald-100 disabled:opacity-50"
                                  >
                                    <FiMic className="w-3 h-3" />
                                    {transcribingId === m.id ? 'Transcribing…' : 'Transcribe'}
                                  </button>
                                </div>
                                {result && (
                                  <div className="text-xs space-y-1">
                                    <p className="text-neutral-700">
                                      <span className="font-semibold text-emerald-700">Transcript:</span>{' '}
                                      {result.transcript}
                                      {result.source && (
                                        <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px]">
                                          {result.source === 'openai' || result.source === 'openai-whisper'
                                        ? 'OpenAI Whisper'
                                        : result.source === 'openrouter'
                                        ? 'OpenRouter'
                                        : result.source === 'groq'
                                        ? 'Groq'
                                        : result.source.startsWith('browser-')
                                        ? 'Browser Whisper'
                                        : result.source}
                                        </span>
                                      )}
                                    </p>
                                    {!isEnglishCode(result.targetLang) && (
                                      <p className="text-neutral-700">
                                        <span className="font-semibold text-emerald-700">
                                          {getLanguageLabel(result.targetLang)}:
                                        </span>{' '}
                                        {result.translated}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return (
                            <div className="whitespace-pre-wrap break-words">
                              {m.variety === 'text' || m.variety === 'chat' || !m.variety ? (
                                <TranslatedText text={media.text} targetLang={translateLang} />
                              ) : (
                                media.text
                              )}
                            </div>
                          );
                        })()}
                        {(() => {
                          const docLink = bubbleExtra?.document_link || bubbleExtra?.link;
                          if (!docLink) return null;
                          return (
                            <a
                              href={docLink}
                              target="_blank"
                              rel="noreferrer"
                              className={`mt-1.5 inline-flex items-center gap-1 text-xs font-medium underline underline-offset-2 break-all ${
                                m.direction === 'out' ? 'text-emerald-100' : 'text-emerald-700'
                              }`}
                            >
                              📄 Open document
                            </a>
                          );
                        })()}
                        {bubbleExtra?.template_preview?.buttons &&
                          Array.isArray(bubbleExtra.template_preview.buttons) &&
                          bubbleExtra.template_preview.buttons.length > 0 && (
                            <div className="mt-2 space-y-1.5">
                              {bubbleExtra.template_preview.buttons.map((btn, idx) => {
                                const url = String(btn.url || '').trim();
                                const label = String(btn.text || 'Open link');
                                return (
                                  <a
                                    key={idx}
                                    href={url || '#'}
                                    target="_blank"
                                    rel="noreferrer"
                                    className={`block text-center text-xs font-medium px-2 py-1.5 rounded-md border transition-colors ${
                                      m.direction === 'out'
                                        ? 'bg-white text-emerald-700 border-white hover:bg-emerald-50'
                                        : 'bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100'
                                    }`}
                                    onClick={(e) => {
                                      if (!url) e.preventDefault();
                                    }}
                                  >
                                    {label}
                                  </a>
                                );
                              })}
                            </div>
                          )}
                        <div className="text-[10px] opacity-70 mt-1 flex items-center gap-1">
                          <span>{fmtLocal(m.created_at)}</span>
                          {m.direction === 'out' && (
                            <span
                              className={getStatusColor(m.delivery_status, blocked)}
                              title={
                                blocked
                                  ? `Blocked / not delivered — ${deliveryFailHint || 'recipient may have blocked this number'}`
                                  : deliveryFailHint
                                    ? `${normalizeDeliveryStatus(m.delivery_status)} — ${deliveryFailHint}`
                                    : normalizeDeliveryStatus(m.delivery_status) || 'sent'
                              }
                            >
                              {getStatusTick(m.delivery_status, blocked)}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => setForwardMessage(m)}
                            className={`ml-1 p-0.5 rounded ${
                              m.direction === 'out'
                                ? 'text-white/70 hover:bg-white/20 hover:text-white'
                                : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800'
                            }`}
                            title="Forward message"
                          >
                            <FiCornerUpRight className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteMessage(m.id)}
                            className={`ml-1 p-0.5 rounded ${
                              m.direction === 'out'
                                ? 'text-white/70 hover:bg-white/20 hover:text-white'
                                : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800'
                            }`}
                            title="Delete message from this app"
                          >
                            <FiTrash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      );
                    })
                  )}
                </div>
                {showScrollDown && (
                  <button
                    type="button"
                    onClick={jumpToLatest}
                    className="absolute right-4 bottom-[88px] z-10 w-9 h-9 rounded-full bg-neutral-900 text-white shadow-lg hover:bg-neutral-800 flex items-center justify-center"
                    title="Jump to latest messages"
                  >
                    <FiChevronDown className="w-5 h-5" />
                  </button>
                )}
                <form
                  onSubmit={handleComposerSubmit}
                  className="p-3 border-t border-neutral-200 bg-white"
                >
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    className="hidden"
                    accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                    onChange={(e) => setMediaFile(e.target.files?.[0] || null)}
                    disabled={!selectedPhone || configured === false || composerSending}
                  />
                  <div className="flex items-center gap-2 min-h-[44px]">
                    <button
                      type="button"
                      onClick={() => attachmentInputRef.current?.click()}
                      disabled={!selectedPhone || configured === false || composerSending}
                      className="shrink-0 p-2 rounded-full text-neutral-600 hover:bg-neutral-100 disabled:opacity-40"
                      title="Attach image, video, audio, or document"
                      aria-label="Attach file"
                    >
                      <FiPaperclip className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      disabled={!selectedPhone || configured === false || composerSending}
                      onMouseDown={() => {
                        micLongPressTriggeredRef.current = false;
                        micLongPressTimerRef.current = setTimeout(() => {
                          micLongPressTriggeredRef.current = true;
                          if (!voiceRecording) startVoiceToText();
                        }, 400);
                      }}
                      onMouseUp={() => {
                        if (micLongPressTimerRef.current) {
                          clearTimeout(micLongPressTimerRef.current);
                          micLongPressTimerRef.current = null;
                        }
                        if (micLongPressTriggeredRef.current) {
                          stopVoiceToText();
                        } else if (voiceRecording) {
                          stopVoiceRecording();
                        } else {
                          startVoiceRecording();
                        }
                      }}
                      onMouseLeave={() => {
                        if (micLongPressTimerRef.current) {
                          clearTimeout(micLongPressTimerRef.current);
                          micLongPressTimerRef.current = null;
                        }
                        if (voiceListening) stopVoiceToText();
                      }}
                      onTouchStart={(e) => {
                        e.preventDefault();
                        micLongPressTriggeredRef.current = false;
                        micLongPressTimerRef.current = setTimeout(() => {
                          micLongPressTriggeredRef.current = true;
                          if (!voiceRecording) startVoiceToText();
                        }, 400);
                      }}
                      onTouchEnd={(e) => {
                        e.preventDefault();
                        if (micLongPressTimerRef.current) {
                          clearTimeout(micLongPressTimerRef.current);
                          micLongPressTimerRef.current = null;
                        }
                        if (micLongPressTriggeredRef.current) {
                          stopVoiceToText();
                        } else if (voiceRecording) {
                          stopVoiceRecording();
                        } else {
                          startVoiceRecording();
                        }
                      }}
                      className={`shrink-0 p-2 rounded-full disabled:opacity-40 select-none ${
                        voiceRecording
                          ? 'bg-red-600 text-white animate-pulse'
                          : voiceListening
                          ? 'bg-emerald-100 text-emerald-600 animate-pulse'
                          : 'text-neutral-600 hover:bg-neutral-100'
                      }`}
                      title={
                        voiceRecording
                          ? `Recording ${recordingSeconds}s (click to stop)`
                          : voiceListening
                          ? 'Listening… (release to stop)'
                          : 'Click to record voice · Long-press for voice-to-text'
                      }
                      aria-label={
                        voiceRecording
                          ? 'Stop recording'
                          : voiceListening
                          ? 'Stop listening'
                          : 'Click to record voice, long-press for voice-to-text'
                      }
                    >
                      {voiceRecording || voiceListening ? (
                        <FiMicOff className="w-5 h-5" />
                      ) : (
                        <FiMic className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => openQuickTemplateModal(selectedPhone)}
                      disabled={!selectedPhone || configured === false || composerSending}
                      className="shrink-0 p-2 rounded-full text-neutral-600 hover:bg-neutral-100 disabled:opacity-40"
                      title="Send a Meta-approved template to this chat"
                      aria-label="Send template"
                    >
                      <FiFileText className="w-5 h-5" />
                    </button>
                    {mediaFile && (
                      <span
                        className="flex max-w-[min(140px,30vw)] items-center gap-1 text-xs text-emerald-900 bg-emerald-50 border border-emerald-100 px-2 py-1 rounded-lg shrink-0"
                        title={mediaFile.name}
                      >
                        <span className="truncate">{mediaFile.name}</span>
                        <button
                          type="button"
                          onClick={() => {
                            setMediaFile(null);
                            if (attachmentInputRef.current) attachmentInputRef.current.value = '';
                          }}
                          className="p-0.5 rounded hover:bg-emerald-100 text-emerald-800"
                          aria-label="Remove attachment"
                        >
                          <FiX className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    )}
                    <input
                      className="flex-1 min-w-0 border border-neutral-200 rounded-full px-4 py-2.5 text-sm bg-white shadow-inner focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                      placeholder={
                        selectedPhone
                          ? mediaFile
                            ? 'Add a caption (optional)…'
                            : 'Type a message…'
                          : 'Select a chat first'
                      }
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      disabled={!selectedPhone || configured === false || composerSending}
                    />
                    <button
                      type="submit"
                      disabled={
                        !selectedPhone ||
                        configured === false ||
                        composerSending ||
                        (!mediaFile && !replyText.trim())
                      }
                      className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-emerald-600 text-white rounded-full text-sm font-semibold disabled:opacity-40 hover:bg-emerald-700"
                    >
                      <FiSend className="w-4 h-4" />
                      {composerSending ? '…' : 'Send'}
                    </button>
                  </div>
                  {!isEnglishCode(translateLang) && selectedPhone && (
                    <div className="mt-2 text-xs text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2 border border-neutral-200">
                      {translatingPreview ? (
                        <span className="italic text-neutral-500">Translating to {getLanguageLabel(translateLang)}…</span>
                      ) : translatedPreview ? (
                        <span>
                          <span className="font-medium text-emerald-700">{getLanguageLabel(translateLang)} preview:</span>{' '}
                          {translatedPreview}
                        </span>
                      ) : (
                        <span className="italic text-neutral-500">Type above to see translation preview</span>
                      )}
                    </div>
                  )}
                  {configured && waSenders.length > 1 && (
                    <p className="text-[10px] text-neutral-400 mt-1.5 leading-snug">
                      Replies use the <strong>Inbox</strong> line in the Chats header — same number as the threads you see here.
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {lightboxImg && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex flex-col items-center justify-center"
          onClick={() => setLightboxImg(null)}
        >
          <div className="absolute top-4 right-4 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <a
              href={lightboxImg}
              download="whatsapp-image"
              className="flex items-center gap-1.5 px-3 py-2 bg-white/15 hover:bg-white/25 rounded-lg text-white text-sm font-medium transition-colors"
              title="Download image"
            >
              <FiDownload className="w-4 h-4" />
              Download
            </a>
            <button
              type="button"
              onClick={() => setLightboxImg(null)}
              className="p-2 bg-white/15 hover:bg-white/25 rounded-lg text-white transition-colors"
              title="Close"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
          <img
            src={lightboxImg}
            alt="Full size preview"
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <p className="mt-3 text-white/50 text-xs">Click outside to close</p>
        </div>
      )}

      {showWhatsappTemplateEditor && (
        <WhatsAppTemplateEditor
          template={templateToEdit}
          onClose={() => setShowWhatsappTemplateEditor(false)}
          onSaved={async (savedTemplate) => {
            await loadWhatsappTemplates();
            if (savedTemplate?.id) setSelectedWhatsappTemplateId(savedTemplate.id);
            if (savedTemplate) {
              setTplName(savedTemplate.metaTemplateName || '');
              setTplLang(savedTemplate.languageCode || 'en_US');
              setTplMode(savedTemplate.templateMode || 'document');
              setTplDocLink(savedTemplate.documentLink || '');
              setTplDocText(savedTemplate.documentText || '');
              setTplSalespersonPhone('');
              setTplAlertPhone('');
            }
          }}
        />
      )}

      {showAIWhatsAppHelper && (
        <AIWhatsAppHelper
          text={tplDocText}
          onClose={() => setShowAIWhatsAppHelper(false)}
          onApplyText={(newText) => {
            setTplDocText(newText || '');
          }}
        />
      )}

      {forwardMessage && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-neutral-900">Forward message</h3>
              <button
                type="button"
                onClick={() => setForwardMessage(null)}
                className="p-1.5 rounded-lg text-neutral-500 hover:bg-neutral-100"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-neutral-600 mb-4 line-clamp-3">
              {forwardMessage.body || `[${forwardMessage.variety}]`}
            </p>
            <p className="text-xs font-semibold text-neutral-700 mb-2">Select a contact</p>
            <div className="max-h-60 overflow-y-auto border border-neutral-200 rounded-xl divide-y divide-neutral-100">
              {conversations.length === 0 ? (
                <p className="p-3 text-sm text-neutral-500">No conversations yet</p>
              ) : (
                conversations.map((c) => (
                  <button
                    key={c.phone_e164}
                    type="button"
                    onClick={() => handleForwardSend(c.phone_e164)}
                    className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-sm"
                  >
                    <span className="font-medium text-neutral-900">{getDisplayLabel(c.phone_e164)}</span>
                    {getContactName(c.phone_e164) && (
                      <span className="ml-1 text-xs text-neutral-500 font-mono">{c.phone_e164}</span>
                    )}
                    {c.last_preview && (
                      <span className="block text-xs text-neutral-500 truncate">{c.last_preview}</span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {editingDisplayName && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-neutral-900">Rename conversation</h3>
              <button
                type="button"
                onClick={() => {
                  setEditingDisplayName(null);
                  setDisplayNameInput('');
                }}
                className="p-1.5 rounded-lg text-neutral-500 hover:bg-neutral-100"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-neutral-500 mb-2">{editingDisplayName}</p>
            <input
              type="text"
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm mb-4"
              placeholder="e.g. Rahul Sharma"
              value={displayNameInput}
              onChange={(e) => setDisplayNameInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveDisplayName();
                if (e.key === 'Escape') {
                  setEditingDisplayName(null);
                  setDisplayNameInput('');
                }
              }}
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setEditingDisplayName(null);
                  setDisplayNameInput('');
                }}
                className="px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveDisplayName}
                disabled={savingDisplayName}
                className="px-4 py-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:opacity-50"
              >
                {savingDisplayName ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppPage;
